import 'x-sender/style'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AiTwotoneSetting } from 'react-icons/ai'
import { BsBorderOuter } from 'react-icons/bs'
import { FiArrowUp, FiChevronDown, FiEdit2, FiPlus, FiSearch, FiSliders, FiTrash2, FiUpload, FiX } from 'react-icons/fi'
import { TbVersionsFilled } from 'react-icons/tb'
import XSender, { AnyTagProps, SelectConfig as XSenderSelectConfig } from 'x-sender'

import { getModelList, ModelListItem } from '@/api/agent'
import { listCategories, listSkillConfigs, SkillCategoryItem, SkillItem, updateSkillConfig } from '@/api/skill'
import { ossClient } from '@/utils/ossClient'

const isImageUrl = (value?: string) => !!value && /^(https?:\/\/|\/|data:image\/)/i.test(value)

type ActionType = 'skillChange' | 'modelSelect' | 'editor'
type ComponentType = 'Radio' | 'Select' | 'Upload'

type SkillComponent = {
  icon: string
  type: ComponentType
  actionType: ActionType
  key: string
  id: string
  name: string
}

type SelectOption = {
  id: string
  name: string
  icon: string
  desc: string
}

type SelectConfigItem = {
  key: string
  dialogTitle: string
  options: SelectOption[]
}

type SkillTemplateItem = {
  title: string
  id: string
  cover: string
  desc: string
  template: unknown[]
}

type SkillDataPayload = {
  components?: SkillComponent[]
  selectConfig?: SelectConfigItem[]
  templates?: unknown[]
  defaultTemplateId?: string
  placeholder?: string
  /** 技能可用的模型 ID 列表，如 ['glm-4.7','doubao-seed'] */
  models?: string[]
  [key: string]: unknown
}

type SkillListItemWithData = SkillItem & {
  data?: string
  defaultTemplateId?: string
  placeholder?: string
  topShow?: boolean
}

type PresetSkillComponent = {
  dedupeKey: string
  component: SkillComponent
  selectConfig?: SelectConfigItem
  sourceSkillName: string
}

type ComponentForm = {
  actionType: ActionType | ''
  type: ComponentType | ''
  icon: string
  key: string
  id: string
  name: string
  dialogTitle: string
  options: SelectOption[]
}

type OptionGroupFormState = {
  key: string
  dialogTitle: string
  options: SelectOption[]
}

const actionTypeOptions: Array<{ value: ActionType; label: string }> = [
  { value: 'skillChange', label: 'skillChange' },
  { value: 'modelSelect', label: 'modelSelect' },
  { value: 'editor', label: 'editor' }
]

const componentTypeOptions: Array<{ value: ComponentType; label: string }> = [
  { value: 'Radio', label: 'Radio' },
  { value: 'Select', label: 'Select' },
  { value: 'Upload', label: 'Upload' }
]

const createEmptyOption = (): SelectOption => ({
  id: '',
  name: '',
  icon: '',
  desc: ''
})

const defaultComponentForm: ComponentForm = {
  actionType: '',
  type: '',
  icon: '',
  key: '',
  id: '',
  name: '',
  dialogTitle: '',
  options: [createEmptyOption()]
}

const createDefaultOptionGroupForm = (): OptionGroupFormState => ({
  key: '',
  dialogTitle: '',
  options: [createEmptyOption()]
})

const normalizeSkillComponent = (value: unknown): SkillComponent | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const record = value as Record<string, unknown>
  const type = String(record.type ?? '')
  const actionType = String(record.actionType ?? '')
  if ((type !== 'Radio' && type !== 'Select' && type !== 'Upload') || (actionType !== 'skillChange' && actionType !== 'modelSelect' && actionType !== 'editor')) {
    return null
  }

  return {
    icon: String(record.icon ?? '').trim(),
    type: type as ComponentType,
    actionType: actionType as ActionType,
    key: String(record.key ?? '').trim(),
    id: String(record.id ?? '').trim(),
    name: String(record.name ?? '').trim()
  }
}

const normalizeSelectConfigItem = (value: unknown): SelectConfigItem | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const record = value as Record<string, unknown>
  const key = String(record.key ?? '').trim()
  if (!key) return null

  const options = Array.isArray(record.options)
    ? record.options
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
        .map((item) => ({
          id: String(item.id ?? '').trim(),
          name: String(item.name ?? '').trim(),
          icon: String(item.icon ?? '').trim(),
          desc: String(item.desc ?? '').trim()
        }))
        .filter((item) => item.id && item.name)
    : []

  return {
    key,
    dialogTitle: String(record.dialogTitle ?? '').trim() || key,
    options
  }
}

const cloneOptions = (options: SelectOption[]): SelectOption[] =>
  options.map((item) => ({
    id: item.id,
    name: item.name,
    icon: item.icon,
    desc: item.desc
  }))

type OptionsEditorProps = {
  title?: string
  options: SelectOption[]
  uploadingIndex: number | null
  onAdd: () => void
  onRemove: (index: number) => void
  onChange: (index: number, field: keyof SelectOption, value: string) => void
  onUpload: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
}

function OptionsEditor({ title = '组件可选项（options）', options, uploadingIndex, onAdd, onRemove, onChange, onUpload }: OptionsEditorProps) {
  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  return (
    <div className="space-y-3">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50"
        >
          <FiPlus className="h-3.5 w-3.5" />
          新增选项
        </button>
      </div>

      {options.map((option, index) => (
        <div key={`option-${index}`} className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs text-gray-500">选项 {index + 1}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={options.length === 1}
              className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <input
              value={option.id}
              onChange={(e) => onChange(index, 'id', e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500"
              placeholder="option.id"
            />
            <input
              value={option.name}
              onChange={(e) => onChange(index, 'name', e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500"
              placeholder="option.name"
            />
            <div className="flex gap-2 md:col-span-2">
              <input
                value={option.icon}
                onChange={(e) => onChange(index, 'icon', e.target.value)}
                className="h-9 flex-1 rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500"
                placeholder="option.icon（可选）"
              />
              <input
                ref={(el) => {
                  inputRefs.current[index] = el
                }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onUpload(index, e)}
              />
              <button
                type="button"
                onClick={() => inputRefs.current[index]?.click()}
                disabled={uploadingIndex === index}
                className="h-9 shrink-0 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiUpload className="h-4 w-4" />
                {uploadingIndex === index ? '上传中...' : '上传图标'}
              </button>
            </div>
            {option.icon && (
              <div className="flex items-center gap-2 md:col-span-2">
                {isImageUrl(option.icon) && <img src={option.icon} alt="选项图标预览" className="h-7 w-7 rounded object-cover ring-1 ring-gray-200" />}
                <span className="truncate text-xs text-gray-500">{option.icon}</span>
              </div>
            )}
            <input
              value={option.desc}
              onChange={(e) => onChange(index, 'desc', e.target.value)}
              className="h-9 rounded-lg border border-gray-200 px-3 text-sm text-gray-800 outline-none transition-colors focus:border-blue-500 md:col-span-2"
              placeholder="option.desc（可选）"
            />
          </div>
        </div>
      ))}
    </div>
  )
}

type TemplateWriteNodeDraft = {
  uid: string
  type: 'Write'
  text: string
}

type TemplateInputNodeDraft = {
  uid: string
  type: 'Input'
  key: string
  placeholder: string
  text: string
}

type TemplateSelectNodeDraft = {
  uid: string
  type: 'Select'
  key: string
  id: string
  name: string
  dialogTitle: string
  options: SelectOption[]
}

type TemplateNodeDraft = TemplateWriteNodeDraft | TemplateInputNodeDraft | TemplateSelectNodeDraft

type TemplateFormState = {
  title: string
  id: string
  cover: string
  desc: string
  nodes: TemplateNodeDraft[]
}

type TemplateNodeEditorState = {
  type: 'Input' | 'Select'
  key: string
  placeholder: string
  text: string
  id: string
  name: string
  dialogTitle: string
  options: SelectOption[]
}

const createUid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

const createRandomTemplateId = (existingIds: Set<string>) => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const length = 10
  const maxAttempts = 100
  for (let i = 0; i < maxAttempts; i += 1) {
    let candidate = ''
    for (let j = 0; j < length; j += 1) {
      candidate += chars[Math.floor(Math.random() * chars.length)]
    }
    if (!existingIds.has(candidate)) return candidate
  }
  return `${Date.now().toString(36).slice(-10).padStart(10, '0')}`
}

const createTemplateWriteNode = (text = ''): TemplateWriteNodeDraft => ({
  uid: createUid(),
  type: 'Write',
  text
})

const createDefaultTemplateForm = (): TemplateFormState => ({
  title: '',
  id: '',
  cover: '',
  desc: '',
  nodes: [createTemplateWriteNode()]
})

const templatePayloadToDraftNodes = (templatePayload: unknown[], selectConfigMap: Record<string, SelectConfigItem>): TemplateNodeDraft[] => {
  if (!Array.isArray(templatePayload)) return [createTemplateWriteNode()]

  const nodes = templatePayload.flatMap<TemplateNodeDraft>((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return []
    const record = item as Record<string, unknown>
    const type = String(record.type ?? '')

    if (type === 'Write') {
      return [
        {
          uid: createUid(),
          type: 'Write',
          text: String(record.text ?? '')
        }
      ]
    }

    if (type === 'Input') {
      return [
        {
          uid: createUid(),
          type: 'Input',
          key: String(record.key ?? ''),
          placeholder: String(record.placeholder ?? ''),
          text: String(record.text ?? '')
        }
      ]
    }

    if (type === 'Select') {
      const key = String(record.key ?? '')
      const id = String(record.id ?? '')
      const name = String(record.name ?? '')
      const matchedConfig = key ? selectConfigMap[key] : undefined
      const options = matchedConfig?.options?.length
        ? cloneOptions(matchedConfig.options)
        : id || name
          ? [
              {
                id,
                name,
                icon: '',
                desc: ''
              }
            ]
          : [createEmptyOption()]

      return [
        {
          uid: createUid(),
          type: 'Select',
          key,
          id,
          name,
          dialogTitle: matchedConfig?.dialogTitle ?? name,
          options
        }
      ]
    }

    return []
  })

  return nodes.length ? nodes : [createTemplateWriteNode()]
}

const createDefaultTemplateNodeEditor = (type: 'Input' | 'Select'): TemplateNodeEditorState => ({
  type,
  key: '',
  placeholder: '',
  text: '',
  id: '',
  name: '',
  dialogTitle: '',
  options: [createEmptyOption()]
})

const templateNodesToChatNodes = (nodes: TemplateNodeDraft[]): AnyTagProps[][] => [
  nodes.map((node) => {
    if (node.type === 'Write') {
      return { type: 'Write', text: node.text }
    }
    if (node.type === 'Input') {
      return {
        type: 'Input',
        key: node.key,
        placeholder: node.placeholder,
        text: node.text
      }
    }
    return {
      type: 'Select',
      key: node.key,
      id: node.id,
      name: node.name
    }
  })
]

const buildTemplateSenderSelectConfig = (nodes: TemplateNodeDraft[]): XSenderSelectConfig[] =>
  nodes.flatMap((node) => {
    if (node.type !== 'Select') return []
    const key = node.key.trim()
    if (!key) return []
    const options = node.options
      .filter((item) => item.id && item.name)
      .map((item) => ({
        id: item.id,
        name: item.name,
        preview: item.icon || undefined
      }))
    if (!options.length) return []
    return [
      {
        key,
        dialogTitle: node.dialogTitle || node.name || '请选择',
        options
      }
    ]
  })

const chatNodesToTemplateNodes = (chatNodes: AnyTagProps[][], previousNodes: TemplateNodeDraft[]): TemplateNodeDraft[] => {
  const selectNodeMeta = new Map<string, { dialogTitle: string; options: SelectOption[] }>()
  previousNodes.forEach((node) => {
    if (node.type !== 'Select') return
    selectNodeMeta.set(node.key, {
      dialogTitle: node.dialogTitle,
      options: node.options.map((item) => ({
        id: item.id,
        name: item.name,
        icon: item.icon,
        desc: item.desc
      }))
    })
  })

  const merged = (Array.isArray(chatNodes) ? chatNodes : []).flatMap((row) => {
    const currentRow = Array.isArray(row) ? row : []
    return currentRow.flatMap<TemplateNodeDraft>((tag) => {
      if (!tag || typeof tag !== 'object') return []
      if (tag.type === 'Write') {
        return [
          {
            uid: createUid(),
            type: 'Write',
            text: tag.text ?? ''
          }
        ]
      }
      if (tag.type === 'Input') {
        return [
          {
            uid: createUid(),
            type: 'Input',
            key: tag.key ?? '',
            placeholder: tag.placeholder ?? '',
            text: tag.text ?? ''
          }
        ]
      }
      if (tag.type === 'Select') {
        const key = tag.key ?? ''
        const preserved = selectNodeMeta.get(key)
        return [
          {
            uid: createUid(),
            type: 'Select',
            key,
            id: tag.id ?? '',
            name: tag.name ?? '',
            dialogTitle: preserved?.dialogTitle ?? tag.name ?? '',
            options: preserved?.options?.length
              ? preserved.options
              : [
                  {
                    id: tag.id ?? '',
                    name: tag.name ?? '',
                    icon: '',
                    desc: ''
                  }
                ]
          }
        ]
      }
      return []
    })
  })

  return merged.length ? merged : [createTemplateWriteNode()]
}

const createDefaultSenderChatNode = (): AnyTagProps[][] => [[{ type: 'Write', text: '' }]]

const buildSenderSelectConfigFromSelectConfig = (selectConfig: SelectConfigItem[]): XSenderSelectConfig[] =>
  selectConfig.flatMap((item) => {
    const key = item.key?.trim()
    if (!key) return []
    const options = (item.options ?? [])
      .map((option) => ({
        id: option.id?.trim() ?? '',
        name: option.name?.trim() ?? '',
        preview: option.icon?.trim() || undefined
      }))
      .filter((option) => option.id && option.name)
    if (!options.length) return []
    return [
      {
        key,
        dialogTitle: item.dialogTitle?.trim() || key,
        options
      }
    ]
  })

const templatePayloadToChatNodes = (templatePayload: unknown[]): AnyTagProps[][] => {
  if (!Array.isArray(templatePayload)) return createDefaultSenderChatNode()

  const row = templatePayload.flatMap<AnyTagProps>((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return []
    const record = item as Record<string, unknown>
    const type = String(record.type ?? '')

    if (type === 'Write') {
      return [
        {
          type: 'Write',
          text: String(record.text ?? '')
        }
      ]
    }

    if (type === 'Input') {
      return [
        {
          type: 'Input',
          key: String(record.key ?? ''),
          placeholder: String(record.placeholder ?? ''),
          text: String(record.text ?? '')
        }
      ]
    }

    if (type === 'Select') {
      return [
        {
          type: 'Select',
          key: String(record.key ?? ''),
          id: String(record.id ?? ''),
          name: String(record.name ?? '')
        }
      ]
    }

    return []
  })

  return row.length ? [row] : createDefaultSenderChatNode()
}

const parseSkillData = (raw: string): SkillDataPayload => {
  if (!raw.trim()) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as SkillDataPayload
    }
    return {}
  } catch {
    return {}
  }
}

const ensureCompleteSkillDataPayload = (payload: SkillDataPayload, fallback?: { defaultTemplateId?: string; placeholder?: string }): SkillDataPayload => {
  const rawModels = payload.models
  const models = Array.isArray(rawModels) && rawModels.every((m) => typeof m === 'string') ? (rawModels as string[]) : []

  return {
    ...payload,
    templates: Array.isArray(payload.templates) ? payload.templates : [],
    defaultTemplateId: typeof payload.defaultTemplateId === 'string' ? payload.defaultTemplateId : (fallback?.defaultTemplateId ?? ''),
    selectConfig: Array.isArray(payload.selectConfig) ? payload.selectConfig : [],
    placeholder: typeof payload.placeholder === 'string' ? payload.placeholder : (fallback?.placeholder ?? ''),
    components: Array.isArray(payload.components) ? payload.components : [],
    models
  }
}

const parseLooseJson = (raw: string): unknown => {
  const trimmed = raw.trim()
  if (!trimmed) throw new Error('请输入要应用的数据结构')

  try {
    return JSON.parse(trimmed)
  } catch {
    const withoutComments = trimmed.replace(/\/\*[\s\S]*?\*\//g, '').replace(/^\s*\/\/.*$/gm, '')

    const withQuotedKeys = withoutComments.replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":')
    const withDoubleQuotes = withQuotedKeys.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_, content: string) => {
      const normalized = content.replace(/\\'/g, "'")
      return JSON.stringify(normalized)
    })
    const withoutTrailingComma = withDoubleQuotes.replace(/,\s*([}\]])/g, '$1')
    return JSON.parse(withoutTrailingComma)
  }
}

type SkillInfo = {
  skillId: string
  name: string
  skillName: string
  description: string
  avatar: string
  defaultTemplateId: string
  placeholder: string
  categoryId: number
  categoryName: string | null
  sort: number
  data: string
  topShow: boolean
}

const defaultSkillInfo: SkillInfo = {
  skillId: '',
  name: '',
  skillName: '',
  description: '',
  avatar: '',
  defaultTemplateId: '',
  placeholder: '',
  categoryId: 0,
  categoryName: null,
  sort: 0,
  data: '',
  topShow: true
}

/** 默认技能结构（音乐生成），用于「重置默认数据」 */
const DEFAULT_SKILL_STRUCTURE_JSON = `[
  {
    "id": "music",
    "name": "音乐生成",
    "defaultTemplateId": "music_template_1",
    "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg",
    "templates": [
      {
        "title": "音乐生成模板1",
        "id": "music_template_1",
        "cover": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg",
        "desc": "我想创作一首歌曲，用 AI 帮我写歌词 ",
        "template": [
          {"type": "Write", "text": "我想创作一首歌曲，用 AI 帮我写歌词 "},
          {"type": "Input", "key": "lyrics_theme", "placeholder": "描述歌词要表达的主题", "text": "默认值"},
          {"type": "Write", "text": " 。这首歌是 "},
          {"type": "Select", "key": "music_style", "id": "pop", "name": "流行"},
          {"type": "Write", "text": " 音乐风格，传达 "},
          {"type": "Select", "key": "emotion", "id": "happy", "name": "快乐"},
          {"type": "Write", "text": " 的情绪，使用 "},
          {"type": "Select", "key": "voice_type", "id": "female", "name": "女声"},
          {"type": "Write", "text": " 音色。"}
        ]
      },
      {
        "title": "音乐生成模板2",
        "id": "music_template_2",
        "cover": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg",
        "desc": "我想创作一首歌曲，用 AI 帮我写歌词 ",
        "template": [{"type": "Write", "text": "我想创作一首歌曲，用 AI 帮我写歌词 "}]
      }
    ],
    "selectConfig": [
      {"key": "music_style", "dialogTitle": "音乐风格", "options": [{"id": "pop", "name": "流行", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "rock", "name": "摇滚", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "jazz", "name": "爵士", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "classical", "name": "古典", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "electronic", "name": "电子", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "folk", "name": "民谣", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "rnb", "name": "R&B", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}]},
      {"key": "emotion", "dialogTitle": "情绪", "options": [{"id": "happy", "name": "快乐", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "sad", "name": "悲伤", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "excited", "name": "激动", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "calm", "name": "平静", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "romantic", "name": "浪漫", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "melancholy", "name": "忧郁", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}]},
      {"key": "voice_type", "dialogTitle": "音色", "options": [{"id": "female", "name": "女声", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "male", "name": "男声", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "mixed", "name": "混声", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "child", "name": "童声", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}]},
      {"key": "skill_change", "dialogTitle": "技能", "options": [{"id": "web", "name": "网页配色", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}, {"id": "video", "name": "创意建造师", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "xxx "}]},
      {"key": "model_select", "dialogTitle": "技能", "options": [{"id": "seedance_2_0_fast", "name": "Seedance 2.0 Fast", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "全能模型1：Seedance 2.0 Fast"}, {"id": "seedance_3_0_fast", "name": "Seedance 3.0 Fast", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "全能模型2：Seedance 3.0 Fast"}, {"id": "seedream_5_0_preview", "name": "Seedream 5.0 Preview", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "Seedream 5.0 Preview"}, {"id": "seedream_4_5", "name": "Seedream 4.5", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "Seedream 4.5"}, {"id": "seedream_4_0", "name": "Seedream 4.0", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "Seedream 4.0"}]},
      {"key": "mode", "dialogTitle": "模式", "options": [{"id": "fast", "name": "快速", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "全能模型1：Seedance 2.0 Fast"}, {"id": "think", "name": "思考", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "全能模型2：Seedance 3.0 Fast"}, {"id": "expert", "name": "专家", "icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "desc": "全能模型2：Seedance 3.0 Fast"}]}
    ],
    "placeholder": "描述你想要的歌曲",
    "components": [
      {"icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "type": "Radio", "actionType": "skillChange", "key": "skill_change", "id": "web", "name": "选择技能"},
      {"icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "type": "Select", "actionType": "modelSelect", "key": "model_select", "id": "seedance_2_0_fast", "name": "选择模型"},
      {"icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "type": "Select", "actionType": "editor", "key": "music_style", "id": "pop", "name": "流行"},
      {"icon": "https://img.art.miaobi.cn/maliang/test/2026-03-19/2030932006044569600/skill/component/1773913439061_btbyvg.svg", "type": "Upload", "actionType": "editor", "key": "", "id": "", "name": "上传"},
    ]
  }
]`

const buildSkillStructureOutput = (info: SkillInfo, extras?: { useCount?: number; added?: boolean }): Record<string, unknown> => {
  const normalizedSkillId = info.skillId.trim()
  const numericSkillId = Number(normalizedSkillId)
  return {
    id: normalizedSkillId && Number.isFinite(numericSkillId) ? numericSkillId : normalizedSkillId,
    name: info.name.trim() || info.skillName.trim(),
    skillName: info.skillName.trim() || info.name.trim(),
    description: info.description.trim(),
    avatar: info.avatar.trim(),
    categoryId: Number.isFinite(Number(info.categoryId)) ? Number(info.categoryId) : 0,
    categoryName: info.categoryName,
    useCount: extras?.useCount ?? 0,
    sort: Number.isFinite(Number(info.sort)) ? Number(info.sort) : 0,
    added: extras?.added ?? false,
    data: info.data ?? '',
    topShow: info.topShow
  }
}

const normalizeSkillInfoForCompare = (info: SkillInfo) => ({
  skillId: info.skillId,
  name: info.name,
  skillName: info.skillName,
  description: info.description,
  avatar: info.avatar,
  defaultTemplateId: info.defaultTemplateId,
  placeholder: info.placeholder,
  categoryId: info.categoryId,
  categoryName: info.categoryName,
  sort: info.sort,
  data: info.data,
  topShow: info.topShow
})

const buildSkillEditSnapshot = (skill: SkillInfo, draft: SkillInfo, structureValue: string) =>
  JSON.stringify({
    skillInfo: normalizeSkillInfoForCompare(skill),
    draftSkillInfo: normalizeSkillInfoForCompare(draft),
    structureEditorValue: structureValue
  })

export default function EditorPage() {
  const [skillInfo, setSkillInfo] = useState<SkillInfo>(defaultSkillInfo)
  const [draftSkillInfo, setDraftSkillInfo] = useState<SkillInfo>(defaultSkillInfo)
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false)
  const [structureEditorValue, setStructureEditorValue] = useState('')
  const [structureEditorError, setStructureEditorError] = useState('')
  const [categories, setCategories] = useState<SkillCategoryItem[]>([])
  const [isCategoryLoading, setIsCategoryLoading] = useState(false)
  const [categoryError, setCategoryError] = useState('')
  const [skillConfigs, setSkillConfigs] = useState<SkillItem[]>([])
  const [isSkillConfigsLoading, setIsSkillConfigsLoading] = useState(false)
  const [skillConfigsError, setSkillConfigsError] = useState('')
  const [selectedSkillConfigId, setSelectedSkillConfigId] = useState<number | null>(null)
  const [selectedSkillInitialSnapshot, setSelectedSkillInitialSnapshot] = useState<string | null>(null)
  const [isSavingSkillData, setIsSavingSkillData] = useState(false)
  const [agentModelList, setAgentModelList] = useState<ModelListItem[]>([])
  const [agentModelListLoading, setAgentModelListLoading] = useState(false)
  const [isAvatarUploading, setIsAvatarUploading] = useState(false)
  const [isSkillAvatarLoadFailed, setIsSkillAvatarLoadFailed] = useState(false)
  const [isComponentModalOpen, setIsComponentModalOpen] = useState(false)
  const [componentForm, setComponentForm] = useState<ComponentForm>(defaultComponentForm)
  const [componentFormError, setComponentFormError] = useState('')
  const [editingComponentIndex, setEditingComponentIndex] = useState<number | null>(null)
  const [isComponentIconUploading, setIsComponentIconUploading] = useState(false)
  const [uploadingOptionIndex, setUploadingOptionIndex] = useState<number | null>(null)
  const [isTemplatePanelOpen, setIsTemplatePanelOpen] = useState(false)
  const [isTemplateFormOpen, setIsTemplateFormOpen] = useState(false)
  const [templateForm, setTemplateForm] = useState<TemplateFormState>(createDefaultTemplateForm)
  const [templateFormError, setTemplateFormError] = useState('')
  const [editingTemplateIndex, setEditingTemplateIndex] = useState<number | null>(null)
  const [isTemplateCoverUploading, setIsTemplateCoverUploading] = useState(false)
  const [isTemplateNodeEditorOpen, setIsTemplateNodeEditorOpen] = useState(false)
  const [templateNodeEditor, setTemplateNodeEditor] = useState<TemplateNodeEditorState>(createDefaultTemplateNodeEditor('Input'))
  const [editingTemplateNodeUid, setEditingTemplateNodeUid] = useState<string | null>(null)
  const [templateNodeEditorError, setTemplateNodeEditorError] = useState('')
  const [uploadingTemplateNodeOptionIndex, setUploadingTemplateNodeOptionIndex] = useState<number | null>(null)
  const [draggingTemplateIndex, setDraggingTemplateIndex] = useState<number | null>(null)
  const [optionLibrary, setOptionLibrary] = useState<SelectConfigItem[]>([])
  const [isOptionGroupModalOpen, setIsOptionGroupModalOpen] = useState(false)
  const [optionGroupForm, setOptionGroupForm] = useState<OptionGroupFormState>(createDefaultOptionGroupForm)
  const [optionGroupFormError, setOptionGroupFormError] = useState('')
  const [editingOptionGroupOriginalKey, setEditingOptionGroupOriginalKey] = useState<string | null>(null)
  const [optionGroupApplyTarget, setOptionGroupApplyTarget] = useState<'component' | 'template' | null>(null)
  const [uploadingOptionGroupIndex, setUploadingOptionGroupIndex] = useState<number | null>(null)
  const [activeTemplateId, setActiveTemplateId] = useState('')
  const [mainSenderChatNode, setMainSenderChatNode] = useState<AnyTagProps[][]>(createDefaultSenderChatNode)
  const [hoveredComponentMenuIndex, setHoveredComponentMenuIndex] = useState<number | null>(null)
  const templateSenderRef = useRef<XSender | null>(null)
  const templateSenderElRef = useRef<HTMLDivElement>(null)
  const mainSenderRef = useRef<XSender | null>(null)
  const mainSenderElRef = useRef<HTMLDivElement>(null)
  const isTemplateSenderSyncingRef = useRef(false)
  const componentMenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const componentMenuTriggerRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const componentMenuContentRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const componentIconInputRef = useRef<HTMLInputElement>(null)
  const templateCoverInputRef = useRef<HTMLInputElement>(null)
  const componentUploadInputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const hasSkillData = Boolean(
    skillInfo.skillId.trim() ||
    skillInfo.name.trim() ||
    skillInfo.skillName.trim() ||
    skillInfo.description.trim() ||
    skillInfo.avatar.trim() ||
    skillInfo.defaultTemplateId.trim() ||
    skillInfo.placeholder.trim() ||
    skillInfo.data.trim()
  )
  const parsedSkillData = useMemo(() => parseSkillData(skillInfo.data), [skillInfo.data])
  const draftDataModels = useMemo(() => {
    const m = parseSkillData(draftSkillInfo.data).models
    if (!Array.isArray(m)) return [] as string[]
    return m.filter((x): x is string => typeof x === 'string')
  }, [draftSkillInfo.data])
  const skillComponents = useMemo(() => (Array.isArray(parsedSkillData.components) ? parsedSkillData.components : []), [parsedSkillData.components])
  const selectedSkillComponentKeys = useMemo(() => new Set(skillComponents.map((item) => item.key)), [skillComponents])
  const presetSkillComponents = useMemo<PresetSkillComponent[]>(() => {
    const dedupeMap = new Map<string, PresetSkillComponent>()

    skillConfigs.forEach((skill) => {
      const parsed = parseSkillData(typeof skill.data === 'string' ? skill.data : '')
      const rawComponents = Array.isArray(parsed.components) ? parsed.components : []
      const rawSelectConfig = Array.isArray(parsed.selectConfig) ? parsed.selectConfig : []
      const selectConfigMapByKey = rawSelectConfig.reduce<Record<string, SelectConfigItem>>((acc, item) => {
        const normalized = normalizeSelectConfigItem(item)
        if (normalized) {
          acc[normalized.key] = normalized
        }
        return acc
      }, {})

      rawComponents.forEach((item) => {
        const component = normalizeSkillComponent(item)
        if (!component || !component.key || !component.id || !component.name) return
        if (dedupeMap.has(component.key)) return

        const matchedSelectConfig = component.type === 'Upload' ? undefined : selectConfigMapByKey[component.key]
        dedupeMap.set(component.key, {
          dedupeKey: component.key,
          component,
          selectConfig: matchedSelectConfig
            ? {
                ...matchedSelectConfig,
                options: cloneOptions(matchedSelectConfig.options)
              }
            : undefined,
          sourceSkillName: String(skill.name ?? skill.skillName ?? skill.id ?? '')
        })
      })
    })

    return Array.from(dedupeMap.values())
  }, [skillConfigs])
  const skillTemplates = useMemo<SkillTemplateItem[]>(() => {
    if (!Array.isArray(parsedSkillData.templates)) return []
    return parsedSkillData.templates
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item))
      .map((item) => ({
        title: String(item.title ?? ''),
        id: String(item.id ?? ''),
        cover: String(item.cover ?? ''),
        desc: String(item.desc ?? ''),
        template: Array.isArray(item.template) ? item.template : []
      }))
  }, [parsedSkillData.templates])
  const [componentValues, setComponentValues] = useState<Record<string, string>>({})
  const [componentUploadPreview, setComponentUploadPreview] = useState<Record<string, string>>({})
  const templateFormRef = useRef(templateForm)
  const currentSkillEditSnapshot = useMemo(() => buildSkillEditSnapshot(skillInfo, draftSkillInfo, structureEditorValue), [skillInfo, draftSkillInfo, structureEditorValue])
  const hasUnsavedSelectedSkillChanges = useMemo(() => {
    if (selectedSkillConfigId === null || !selectedSkillInitialSnapshot) return false
    return currentSkillEditSnapshot !== selectedSkillInitialSnapshot
  }, [selectedSkillConfigId, selectedSkillInitialSnapshot, currentSkillEditSnapshot])
  const senderSelectConfig = useMemo(() => {
    const selectConfig = Array.isArray(parsedSkillData.selectConfig) ? parsedSkillData.selectConfig : []
    return buildSenderSelectConfigFromSelectConfig(selectConfig)
  }, [parsedSkillData.selectConfig])
  const selectConfigMap = useMemo(() => {
    const selectConfig = Array.isArray(parsedSkillData.selectConfig) ? parsedSkillData.selectConfig : []
    return selectConfig.reduce<Record<string, SelectConfigItem>>((acc, item) => {
      acc[item.key] = item
      return acc
    }, {})
  }, [parsedSkillData.selectConfig])

  const clearComponentMenuCloseTimer = () => {
    if (componentMenuCloseTimerRef.current) {
      clearTimeout(componentMenuCloseTimerRef.current)
      componentMenuCloseTimerRef.current = null
    }
  }

  const openComponentMenu = (index: number) => {
    clearComponentMenuCloseTimer()
    setHoveredComponentMenuIndex(index)
  }

  const isPointerInComponentMenu = (index: number, relatedTarget: EventTarget | null) => {
    if (!(relatedTarget instanceof Node)) return false
    if (componentMenuTriggerRefs.current[index]?.contains(relatedTarget)) return true
    if (componentMenuContentRefs.current[index]?.contains(relatedTarget)) return true
    return false
  }

  const scheduleCloseComponentMenu = (index: number) => {
    clearComponentMenuCloseTimer()
    componentMenuCloseTimerRef.current = setTimeout(() => {
      setHoveredComponentMenuIndex((prev) => (prev === index ? null : prev))
    }, 180)
  }

  useEffect(() => {
    setIsSkillAvatarLoadFailed(false)
  }, [skillInfo.avatar])

  useEffect(() => {
    return () => {
      clearComponentMenuCloseTimer()
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchCategories = async () => {
      setIsCategoryLoading(true)
      setCategoryError('')
      try {
        const res = await listCategories()
        const nextCategories = (res.data ?? []).slice().sort((a, b) => a.sort - b.sort)
        if (!cancelled) {
          setCategories(nextCategories)
        }
      } catch (error) {
        console.error('Failed to load skill categories:', error)
        if (!cancelled) {
          setCategoryError('技能分类加载失败')
        }
      } finally {
        if (!cancelled) {
          setIsCategoryLoading(false)
        }
      }
    }

    void fetchCategories()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const fetchSkillConfigs = async () => {
      setIsSkillConfigsLoading(true)
      setSkillConfigsError('')
      try {
        const res = await listSkillConfigs()

        if (!cancelled) {
          setSkillConfigs(res.data)
        }
      } catch (error) {
        console.error('Failed to load skill configs:', error)
        if (!cancelled) {
          setSkillConfigsError('技能列表加载失败')
        }
      } finally {
        if (!cancelled) {
          setIsSkillConfigsLoading(false)
        }
      }
    }

    void fetchSkillConfigs()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!isSkillModalOpen) return
    let cancelled = false

    const fetchAgentModels = async () => {
      setAgentModelListLoading(true)
      try {
        const res = await getModelList()
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : []
        setAgentModelList(list)
      } catch (error) {
        console.error('Failed to load agent model list:', error)
        if (!cancelled) {
          setAgentModelList([])
        }
      } finally {
        if (!cancelled) {
          setAgentModelListLoading(false)
        }
      }
    }

    void fetchAgentModels()

    return () => {
      cancelled = true
    }
  }, [isSkillModalOpen])

  useEffect(() => {
    setComponentValues((prev) => {
      const nextValues: Record<string, string> = {}
      skillComponents.forEach((component) => {
        const options = selectConfigMap[component.key]?.options ?? []
        const prevValue = prev[component.key]

        if (component.type === 'Upload') {
          nextValues[component.key] = prevValue ?? component.id ?? ''
          return
        }

        const fallbackValue = component.id || options[0]?.id || ''
        const isPrevValueValid = prevValue ? options.some((option) => option.id === prevValue) : false
        nextValues[component.key] = isPrevValueValid ? prevValue : fallbackValue
      })
      return nextValues
    })
  }, [skillComponents, selectConfigMap])

  useEffect(() => {
    const source = Array.isArray(parsedSkillData.selectConfig) ? parsedSkillData.selectConfig : []
    setOptionLibrary(
      source.map((item) => ({
        key: item.key,
        dialogTitle: item.dialogTitle,
        options: cloneOptions(item.options ?? [])
      }))
    )
  }, [parsedSkillData.selectConfig])

  useEffect(() => {
    templateFormRef.current = templateForm
  }, [templateForm])

  useEffect(() => {
    if (!mainSenderElRef.current) return
    const sender = new XSender(mainSenderElRef.current, {
      placeholder: '描述你想要的图片',
      autoFocus: false,
      tipConfig: { backspace: true }
    })
    mainSenderRef.current = sender

    void sender.reset({
      chatNode: createDefaultSenderChatNode()
    })

    return () => {
      sender.destroy()
      mainSenderRef.current = null
    }
  }, [])

  useEffect(() => {
    const sender = mainSenderRef.current
    if (!sender) return
    sender.updateConfig({
      selectConfig: senderSelectConfig
    })
  }, [senderSelectConfig])

  useEffect(() => {
    const sender = mainSenderRef.current
    if (!sender) return
    void sender.reset({
      chatNode: mainSenderChatNode
    })
  }, [mainSenderChatNode])

  useEffect(() => {
    if (!isTemplateFormOpen || !templateSenderElRef.current) return

    const sender = new XSender(templateSenderElRef.current, {
      placeholder: '输入文案，或通过下方按钮添加输入框/下拉框组件',
      autoFocus: false,
      tipConfig: { backspace: true }
    })
    templateSenderRef.current = sender

    sender.updateConfig({
      selectConfig: buildTemplateSenderSelectConfig(templateFormRef.current.nodes)
    })
    void sender.reset({
      chatNode: templateNodesToChatNodes(templateFormRef.current.nodes)
    })

    const busKey = Symbol('template-editor')
    sender.bus.on(busKey, XSender.EventSet.EVENT_COMMON_CHANGE, () => {
      const model = sender.getModel()
      isTemplateSenderSyncingRef.current = true
      setTemplateForm((prev) => ({
        ...prev,
        nodes: chatNodesToTemplateNodes(model, prev.nodes)
      }))
    })

    return () => {
      sender.destroy()
      templateSenderRef.current = null
    }
  }, [isTemplateFormOpen])

  useEffect(() => {
    if (!isTemplateFormOpen) return
    const sender = templateSenderRef.current
    if (!sender) return
    if (isTemplateSenderSyncingRef.current) {
      isTemplateSenderSyncingRef.current = false
      return
    }
    sender.updateConfig({
      selectConfig: buildTemplateSenderSelectConfig(templateForm.nodes)
    })
    void sender.reset({
      chatNode: templateNodesToChatNodes(templateForm.nodes)
    })
  }, [templateForm.nodes, isTemplateFormOpen])

  const handleOpenSkillEditor = () => {
    setDraftSkillInfo(skillInfo)
    setStructureEditorValue(JSON.stringify(buildSkillStructureOutput(skillInfo), null, 2))
    setStructureEditorError('')
    setIsSkillModalOpen(true)
  }

  const handleSelectSkillFromList = (skill: SkillListItemWithData) => {
    if (selectedSkillConfigId !== null && selectedSkillConfigId !== skill.id && hasUnsavedSelectedSkillChanges) {
      const shouldSwitch = window.confirm('当前编辑的技能数据未保存，切换后将恢复未修改状态，确认切换吗？')
      if (!shouldSwitch) return
    }

    setSelectedSkillConfigId(skill.id)

    const nextSkillInfo: SkillInfo = {
      ...defaultSkillInfo,
      skillId: String(skill.id ?? ''),
      name: skill.name ?? '',
      skillName: skill.skillName ?? skill.name ?? '',
      description: skill.description ?? '',
      avatar: skill.avatar ?? '',
      categoryId: skill.categoryId ?? 0,
      categoryName: skill.categoryName ?? null,
      sort: skill.sort ?? 0,
      data: skill.data ?? '',
      defaultTemplateId: skill.defaultTemplateId ?? '',
      placeholder: skill.placeholder ?? '',
      topShow: typeof skill.topShow === 'boolean' ? skill.topShow : true
    }

    const nextStructureValue = JSON.stringify(
      buildSkillStructureOutput(nextSkillInfo, {
        useCount: skill.useCount,
        added: skill.added
      }),
      null,
      2
    )

    setSkillInfo(nextSkillInfo)
    setDraftSkillInfo(nextSkillInfo)
    setStructureEditorValue(nextStructureValue)
    setSelectedSkillInitialSnapshot(buildSkillEditSnapshot(nextSkillInfo, nextSkillInfo, nextStructureValue))
    setStructureEditorError('')

    const parsed = parseSkillData(nextSkillInfo.data)
    const templates = Array.isArray(parsed.templates) ? parsed.templates : []
    const targetTemplate =
      templates.find(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item) && String((item as Record<string, unknown>).id ?? '') === nextSkillInfo.defaultTemplateId
      ) ?? templates[0]

    if (targetTemplate && typeof targetTemplate === 'object' && !Array.isArray(targetTemplate)) {
      const templateValue = (targetTemplate as { template?: unknown }).template
      const templatePayload = Array.isArray(templateValue) ? templateValue : []
      setMainSenderChatNode(templatePayloadToChatNodes(templatePayload))
      setActiveTemplateId(String((targetTemplate as { id?: unknown }).id ?? ''))
      return
    }

    setMainSenderChatNode(createDefaultSenderChatNode())
    setActiveTemplateId('')
  }

  const handleSaveSkillInfo = async () => {
    if (selectedSkillConfigId === null) {
      window.alert('请先选择技能后再保存')
      return
    }

    setIsSavingSkillData(true)
    try {
      const normalizedDataPayload = ensureCompleteSkillDataPayload(parseSkillData(draftSkillInfo.data), {
        defaultTemplateId: draftSkillInfo.defaultTemplateId,
        placeholder: draftSkillInfo.placeholder
      })
      const normalizedData = JSON.stringify(normalizedDataPayload, null, 2)
      await updateSkillConfig(selectedSkillConfigId, normalizedDataPayload)

      const nextSkillInfo: SkillInfo = {
        ...skillInfo,
        data: normalizedData
      }
      const selectedSkill = skillConfigs.find((item) => item.id === selectedSkillConfigId)
      const nextStructureValue = JSON.stringify(
        buildSkillStructureOutput(nextSkillInfo, {
          useCount: selectedSkill?.useCount ?? 0,
          added: selectedSkill?.added ?? false
        }),
        null,
        2
      )

      setSkillInfo(nextSkillInfo)
      setDraftSkillInfo(nextSkillInfo)
      setSkillConfigs((prev) =>
        prev.map((item) =>
          item.id === selectedSkillConfigId
            ? {
                ...item,
                data: normalizedData
              }
            : item
        )
      )
      setStructureEditorValue(nextStructureValue)
      setSelectedSkillInitialSnapshot(buildSkillEditSnapshot(nextSkillInfo, nextSkillInfo, nextStructureValue))
      setIsSkillModalOpen(false)
    } catch (error) {
      console.error('Save skill data failed:', error)
      window.alert('技能数据保存失败，请稍后重试')
    } finally {
      setIsSavingSkillData(false)
    }
  }

  const toggleDraftDataModel = useCallback((modelId: string) => {
    setDraftSkillInfo((prev) => {
      const payload = parseSkillData(prev.data)
      const raw = payload.models
      const cur = Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : []
      const nextModels = cur.includes(modelId) ? cur.filter((id) => id !== modelId) : [...cur, modelId]
      const nextPayload = { ...payload, models: nextModels }
      return { ...prev, data: JSON.stringify(nextPayload, null, 2) }
    })
  }, [])

  const handleFillStructureFromCurrent = () => {
    setStructureEditorValue(JSON.stringify(buildSkillStructureOutput(draftSkillInfo), null, 2))
    setStructureEditorError('')
  }

  const applySkillStructurePayload = (record: Record<string, unknown>) => {
    // 新结构（id/name/skillName/.../data/topShow）优先兼容
    if ('data' in record && !('templates' in record) && !('selectConfig' in record) && !('components' in record)) {
      const rawData = record.data
      const normalizedData = typeof rawData === 'string' ? rawData : rawData == null ? '' : JSON.stringify(rawData, null, 2)

      const nextSkillInfo: SkillInfo = {
        ...draftSkillInfo,
        skillId: String(record.id ?? draftSkillInfo.skillId).trim(),
        name: String(record.name ?? draftSkillInfo.name).trim(),
        skillName: String(record.skillName ?? draftSkillInfo.skillName ?? record.name ?? draftSkillInfo.name).trim(),
        description: String(record.description ?? draftSkillInfo.description).trim(),
        avatar: String(record.avatar ?? draftSkillInfo.avatar).trim(),
        categoryId: Number.isFinite(Number(record.categoryId)) ? Number(record.categoryId) : draftSkillInfo.categoryId,
        categoryName: record.categoryName == null ? null : String(record.categoryName).trim(),
        sort: Number.isFinite(Number(record.sort)) ? Number(record.sort) : draftSkillInfo.sort,
        data: normalizedData,
        topShow: typeof record.topShow === 'boolean' ? record.topShow : draftSkillInfo.topShow
      }

      setDraftSkillInfo(nextSkillInfo)
      setSkillInfo(nextSkillInfo)
      setStructureEditorValue(
        JSON.stringify(
          buildSkillStructureOutput(nextSkillInfo, {
            useCount: Number.isFinite(Number(record.useCount)) ? Number(record.useCount) : 0,
            added: typeof record.added === 'boolean' ? record.added : false
          }),
          null,
          2
        )
      )
      setStructureEditorError('')

      const parsed = parseSkillData(nextSkillInfo.data)
      const templates = Array.isArray(parsed.templates) ? parsed.templates : []
      const targetTemplate =
        templates.find(
          (item): item is Record<string, unknown> =>
            !!item && typeof item === 'object' && !Array.isArray(item) && String((item as Record<string, unknown>).id ?? '') === nextSkillInfo.defaultTemplateId
        ) ?? templates[0]

      if (targetTemplate && typeof targetTemplate === 'object' && !Array.isArray(targetTemplate)) {
        const templateValue = (targetTemplate as { template?: unknown }).template
        const templatePayload = Array.isArray(templateValue) ? templateValue : []
        setMainSenderChatNode(templatePayloadToChatNodes(templatePayload))
        setActiveTemplateId(String((targetTemplate as { id?: unknown }).id ?? ''))
      } else {
        setMainSenderChatNode(createDefaultSenderChatNode())
        setActiveTemplateId('')
      }
      return
    }

    const name = String(record.name ?? '')
    const skillId = String(record.id ?? '')
    const icon = String(record.icon ?? '')
    const defaultTemplateId = String(record.defaultTemplateId ?? '')
    const placeholder = String(record.placeholder ?? '')

    const components: SkillComponent[] = Array.isArray(record.components)
      ? record.components
          .filter((item) => !!item && typeof item === 'object' && !Array.isArray(item))
          .map((item) => {
            const component = item as Record<string, unknown>
            return {
              icon: String(component.icon ?? ''),
              type: String(component.type ?? '') as ComponentType,
              actionType: String(component.actionType ?? '') as ActionType,
              key: String(component.key ?? ''),
              id: String(component.id ?? ''),
              name: String(component.name ?? '')
            }
          })
          .filter((item) => item.type === 'Radio' || item.type === 'Select' || item.type === 'Upload')
          .filter((item) => item.actionType === 'skillChange' || item.actionType === 'modelSelect' || item.actionType === 'editor')
      : []

    const selectConfig: SelectConfigItem[] = Array.isArray(record.selectConfig)
      ? record.selectConfig
          .filter((item) => !!item && typeof item === 'object' && !Array.isArray(item))
          .map((item) => {
            const config = item as Record<string, unknown>
            const options = Array.isArray(config.options)
              ? config.options
                  .filter((option) => !!option && typeof option === 'object' && !Array.isArray(option))
                  .map((option) => {
                    const optionRecord = option as Record<string, unknown>
                    return {
                      id: String(optionRecord.id ?? ''),
                      name: String(optionRecord.name ?? ''),
                      icon: String(optionRecord.icon ?? ''),
                      desc: String(optionRecord.desc ?? '')
                    }
                  })
              : []
            return {
              key: String(config.key ?? ''),
              dialogTitle: String(config.dialogTitle ?? ''),
              options
            }
          })
          .filter((item) => item.key.trim())
      : []

    const templates = Array.isArray(record.templates)
      ? record.templates
          .filter((item) => !!item && typeof item === 'object' && !Array.isArray(item))
          .map((item) => {
            const template = item as Record<string, unknown>
            return {
              title: String(template.title ?? ''),
              id: String(template.id ?? ''),
              cover: String(template.cover ?? ''),
              desc: String(template.desc ?? ''),
              template: Array.isArray(template.template) ? template.template : []
            }
          })
      : []

    const nextData: SkillDataPayload = {
      components,
      selectConfig,
      templates
    }

    const nextSkillInfo: SkillInfo = {
      ...draftSkillInfo,
      skillId: skillId.trim() || draftSkillInfo.skillId.trim() || draftSkillInfo.skillName.trim(),
      name: name.trim() || draftSkillInfo.name.trim(),
      skillName: draftSkillInfo.skillName.trim() || skillId.trim(),
      avatar: icon.trim() || draftSkillInfo.avatar.trim(),
      defaultTemplateId: defaultTemplateId.trim(),
      placeholder: placeholder.trim(),
      data: JSON.stringify(nextData, null, 2)
    }

    setDraftSkillInfo(nextSkillInfo)
    setSkillInfo(nextSkillInfo)
    setStructureEditorValue(JSON.stringify(buildSkillStructureOutput(nextSkillInfo), null, 2))
    setStructureEditorError('')

    const targetTemplate = templates.find((item) => item.id === (defaultTemplateId.trim() || '')) ?? templates[0]
    if (targetTemplate) {
      setMainSenderChatNode(templatePayloadToChatNodes(targetTemplate.template))
      setActiveTemplateId(targetTemplate.id)
    } else {
      setMainSenderChatNode(createDefaultSenderChatNode())
      setActiveTemplateId('')
    }
  }

  const handleApplyPastedStructure = () => {
    try {
      const parsed = parseLooseJson(structureEditorValue)
      const payload = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        setStructureEditorError('数据结构格式不正确')
        return
      }
      applySkillStructurePayload(payload as Record<string, unknown>)
    } catch (error) {
      console.error('Apply structure failed:', error)
      setStructureEditorError('解析失败，请检查粘贴结构格式')
    }
  }

  const handleResetDefaultSkillData = () => {
    try {
      const parsed = JSON.parse(DEFAULT_SKILL_STRUCTURE_JSON) as unknown
      const payload = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        setStructureEditorError('默认数据格式错误')
        return
      }
      applySkillStructurePayload(payload as Record<string, unknown>)
    } catch {
      setStructureEditorError('默认数据解析失败')
    }
  }

  const handleClearSkillData = () => {
    setDraftSkillInfo(defaultSkillInfo)
    setSkillInfo(defaultSkillInfo)
    setStructureEditorValue('')
    setStructureEditorError('')
    setMainSenderChatNode(createDefaultSenderChatNode())
    setActiveTemplateId('')
  }

  const handleCategoryChange = (categoryId: number) => {
    const matchedCategory = categories.find((item) => item.id === categoryId)
    setDraftSkillInfo((prev) => ({
      ...prev,
      categoryId,
      categoryName: matchedCategory?.name ?? null
    }))
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsAvatarUploading(true)
    try {
      const url = await ossClient.upload(file, 'skill/avatar')
      setDraftSkillInfo((prev) => ({
        ...prev,
        avatar: url
      }))
    } catch (error) {
      console.error('Avatar upload failed:', error)
    } finally {
      setIsAvatarUploading(false)
      e.target.value = ''
    }
  }

  const handleComponentIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsComponentIconUploading(true)
    try {
      const url = await ossClient.upload(file, 'skill/component')
      setComponentForm((prev) => ({
        ...prev,
        icon: url
      }))
    } catch (error) {
      console.error('Component icon upload failed:', error)
    } finally {
      setIsComponentIconUploading(false)
      e.target.value = ''
    }
  }

  const handleOpenComponentModal = () => {
    setComponentForm(defaultComponentForm)
    setComponentFormError('')
    setEditingComponentIndex(null)
    setIsComponentIconUploading(false)
    setUploadingOptionIndex(null)
    setIsComponentModalOpen(true)
  }

  const handleOpenComponentEditor = (index: number) => {
    const targetComponent = skillComponents[index]
    if (!targetComponent) return
    const currentSelectConfig = Array.isArray(parsedSkillData.selectConfig) ? parsedSkillData.selectConfig : []
    const matchedConfig = currentSelectConfig.find((item) => item.key === targetComponent.key)
    setComponentForm({
      actionType: targetComponent.actionType,
      type: targetComponent.type,
      icon: targetComponent.icon ?? '',
      key: targetComponent.key,
      id: targetComponent.id,
      name: targetComponent.name,
      dialogTitle: matchedConfig?.dialogTitle ?? targetComponent.name,
      options: matchedConfig?.options?.length
        ? matchedConfig.options.map((item) => ({
            id: item.id ?? '',
            name: item.name ?? '',
            icon: item.icon ?? '',
            desc: item.desc ?? ''
          }))
        : [createEmptyOption()]
    })
    setEditingComponentIndex(index)
    setComponentFormError('')
    setIsComponentIconUploading(false)
    setUploadingOptionIndex(null)
    setIsComponentModalOpen(true)
  }

  const handleDeleteComponent = (index: number) => {
    const currentData = parseSkillData(skillInfo.data)
    const currentComponents = Array.isArray(currentData.components) ? currentData.components : []
    const target = currentComponents[index]
    if (!target) return

    if (!window.confirm(`确认删除组件「${target.name || target.key || target.type}」吗？`)) {
      return
    }

    const nextComponents = currentComponents.filter((_, itemIndex) => itemIndex !== index)
    const nextData: SkillDataPayload = {
      ...currentData,
      components: nextComponents
    }

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(nextData, null, 2)
    }))

    setComponentValues((prev) => {
      const next = { ...prev }
      delete next[target.key]
      return next
    })
    setComponentUploadPreview((prev) => {
      const next = { ...prev }
      const previewUrl = next[target.key]
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
      }
      delete next[target.key]
      return next
    })
  }

  const handleComponentOptionChange = (index: number, field: keyof SelectOption, value: string) => {
    setComponentForm((prev) => ({
      ...prev,
      options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    }))
  }

  const handleAddComponentOption = () => {
    setComponentForm((prev) => ({
      ...prev,
      options: [...prev.options, createEmptyOption()]
    }))
  }

  const handleRemoveComponentOption = (index: number) => {
    setComponentForm((prev) => ({
      ...prev,
      options: prev.options.length === 1 ? prev.options : prev.options.filter((_, itemIndex) => itemIndex !== index)
    }))
  }

  const handleOptionIconUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingOptionIndex(index)
    try {
      const url = await ossClient.upload(file, 'skill/options')
      setComponentForm((prev) => ({
        ...prev,
        options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, icon: url } : item))
      }))
    } catch (error) {
      console.error('Option icon upload failed:', error)
    } finally {
      setUploadingOptionIndex(null)
      e.target.value = ''
    }
  }

  const upsertOptionLibrary = (group: SelectConfigItem) => {
    setOptionLibrary((prev) => {
      const exists = prev.some((item) => item.key === group.key)
      if (exists) {
        return prev.map((item) => (item.key === group.key ? { ...group, options: cloneOptions(group.options) } : item))
      }
      return [...prev, { ...group, options: cloneOptions(group.options) }]
    })
  }

  const handleApplyOptionGroupToComponent = (groupKey: string) => {
    const group = optionLibrary.find((item) => item.key === groupKey)
    if (!group) return
    const firstOption = group.options[0]
    setComponentForm((prev) => ({
      ...prev,
      key: group.key,
      dialogTitle: group.dialogTitle,
      id: prev.id || firstOption?.id || '',
      name: prev.name || firstOption?.name || '',
      options: cloneOptions(group.options)
    }))
  }

  const handleApplyOptionGroupToTemplate = (groupKey: string) => {
    const group = optionLibrary.find((item) => item.key === groupKey)
    if (!group) return
    const firstOption = group.options[0]
    setTemplateNodeEditor((prev) => ({
      ...prev,
      key: group.key,
      dialogTitle: group.dialogTitle,
      id: prev.id || firstOption?.id || '',
      name: prev.name || firstOption?.name || '',
      options: cloneOptions(group.options)
    }))
  }

  const openOptionGroupModal = (target: 'component' | 'template') => {
    const sourceKey = target === 'component' ? componentForm.key.trim() : templateNodeEditor.key.trim()
    const sourceDialogTitle = target === 'component' ? componentForm.dialogTitle.trim() : templateNodeEditor.dialogTitle.trim()
    const sourceOptions = target === 'component' ? componentForm.options : templateNodeEditor.options
    const matched = sourceKey ? optionLibrary.find((item) => item.key === sourceKey) : undefined

    setOptionGroupApplyTarget(target)
    setEditingOptionGroupOriginalKey(matched?.key ?? null)
    setOptionGroupForm({
      key: matched?.key ?? sourceKey,
      dialogTitle: matched?.dialogTitle ?? sourceDialogTitle,
      options: cloneOptions(matched?.options?.length ? matched.options : sourceOptions.length ? sourceOptions : [createEmptyOption()])
    })
    setOptionGroupFormError('')
    setUploadingOptionGroupIndex(null)
    setIsOptionGroupModalOpen(true)
  }

  const handleOptionGroupOptionChange = (index: number, field: keyof SelectOption, value: string) => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    }))
  }

  const handleAddOptionGroupOption = () => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: [...prev.options, createEmptyOption()]
    }))
  }

  const handleRemoveOptionGroupOption = (index: number) => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: prev.options.length === 1 ? prev.options : prev.options.filter((_, itemIndex) => itemIndex !== index)
    }))
  }

  const handleOptionGroupIconUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingOptionGroupIndex(index)
    try {
      const url = await ossClient.upload(file, 'skill/options')
      setOptionGroupForm((prev) => ({
        ...prev,
        options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, icon: url } : item))
      }))
    } catch (error) {
      console.error('Option group icon upload failed:', error)
    } finally {
      setUploadingOptionGroupIndex(null)
      e.target.value = ''
    }
  }

  const handleSaveOptionGroup = () => {
    const normalizedKey = optionGroupForm.key.trim()
    const normalizedDialogTitle = optionGroupForm.dialogTitle.trim()
    if (!normalizedKey) {
      setOptionGroupFormError('options key 必填')
      return
    }

    const cleanedOptions = optionGroupForm.options
      .map((item) => ({
        id: item.id.trim(),
        name: item.name.trim(),
        icon: item.icon.trim(),
        desc: item.desc.trim()
      }))
      .filter((item) => item.id || item.name || item.icon || item.desc)

    if (cleanedOptions.length === 0 || cleanedOptions.some((item) => !item.id || !item.name)) {
      setOptionGroupFormError('options 至少一项，且每项 id/name 必填')
      return
    }

    const hasDuplicate = optionLibrary.some((item) => item.key === normalizedKey && item.key !== editingOptionGroupOriginalKey)
    if (hasDuplicate) {
      setOptionGroupFormError(`options key "${normalizedKey}" 已存在`)
      return
    }

    if (editingOptionGroupOriginalKey && editingOptionGroupOriginalKey !== normalizedKey) {
      setOptionLibrary((prev) => prev.filter((item) => item.key !== editingOptionGroupOriginalKey))
    }

    const nextGroup: SelectConfigItem = {
      key: normalizedKey,
      dialogTitle: normalizedDialogTitle || normalizedKey,
      options: cleanedOptions
    }
    upsertOptionLibrary(nextGroup)
    setSkillInfo((prev) => {
      const currentData = parseSkillData(prev.data)
      const currentSelectConfig = Array.isArray(currentData.selectConfig) ? currentData.selectConfig : []
      const nextSelectConfig = currentSelectConfig.filter((item) => {
        if (item.key === normalizedKey) return false
        if (editingOptionGroupOriginalKey && item.key === editingOptionGroupOriginalKey) return false
        return true
      })
      return {
        ...prev,
        data: JSON.stringify(
          {
            ...currentData,
            selectConfig: [...nextSelectConfig, nextGroup]
          },
          null,
          2
        )
      }
    })

    if (optionGroupApplyTarget === 'component') {
      const firstOption = nextGroup.options[0]
      setComponentForm((prev) => ({
        ...prev,
        key: nextGroup.key,
        dialogTitle: nextGroup.dialogTitle,
        id: prev.id || firstOption?.id || '',
        name: prev.name || firstOption?.name || '',
        options: cloneOptions(nextGroup.options)
      }))
    } else if (optionGroupApplyTarget === 'template') {
      const firstOption = nextGroup.options[0]
      setTemplateNodeEditor((prev) => ({
        ...prev,
        key: nextGroup.key,
        dialogTitle: nextGroup.dialogTitle,
        id: prev.id || firstOption?.id || '',
        name: prev.name || firstOption?.name || '',
        options: cloneOptions(nextGroup.options)
      }))
    }

    setIsOptionGroupModalOpen(false)
    setOptionGroupFormError('')
    setEditingOptionGroupOriginalKey(null)
    setOptionGroupApplyTarget(null)
    setUploadingOptionGroupIndex(null)
  }

  const handleConfirmAddComponent = () => {
    const normalizedActionType = componentForm.actionType
    const normalizedType = componentForm.type
    const normalizedIcon = componentForm.icon.trim()
    const normalizedKey = componentForm.key.trim()
    const normalizedId = componentForm.id.trim()
    const normalizedName = componentForm.name.trim()
    const normalizedDialogTitle = componentForm.dialogTitle.trim()

    if (!normalizedActionType) {
      setComponentFormError('请先选择执行类型')
      return
    }
    if (!normalizedType) {
      setComponentFormError('请选择组件类型')
      return
    }
    if (!normalizedKey || !normalizedId || !normalizedName) {
      setComponentFormError('请完整填写 key、id、name')
      return
    }

    const cleanedOptions = componentForm.options
      .map((item) => ({
        id: item.id.trim(),
        name: item.name.trim(),
        icon: item.icon.trim(),
        desc: item.desc.trim()
      }))
      .filter((item) => item.id || item.name || item.icon || item.desc)

    if (normalizedType !== 'Upload' && cleanedOptions.length === 0) {
      setComponentFormError('当前组件类型至少需要一个可选项')
      return
    }

    if (normalizedType !== 'Upload' && cleanedOptions.some((item) => !item.id || !item.name)) {
      setComponentFormError('可选项中的 id 与 name 为必填')
      return
    }

    const currentData = parseSkillData(skillInfo.data)
    const currentComponents = Array.isArray(currentData.components) ? currentData.components : []
    const currentSelectConfig = Array.isArray(currentData.selectConfig) ? currentData.selectConfig : []

    const editingTarget = editingComponentIndex !== null ? currentComponents[editingComponentIndex] : null
    const hasDuplicateComponent = currentComponents.some((item, index) => item.key === normalizedKey && index !== editingComponentIndex)
    if (hasDuplicateComponent) {
      setComponentFormError(`组件 key "${normalizedKey}" 已存在`)
      return
    }

    const nextComponent: SkillComponent = {
      icon: normalizedIcon,
      type: normalizedType,
      actionType: normalizedActionType,
      key: normalizedKey,
      id: normalizedId,
      name: normalizedName
    }

    const nextComponents: SkillComponent[] =
      editingComponentIndex === null ? [...currentComponents, nextComponent] : currentComponents.map((item, index) => (index === editingComponentIndex ? nextComponent : item))

    const nextSelectConfigBase = currentSelectConfig.filter((item) => item.key !== normalizedKey && item.key !== editingTarget?.key)
    const nextSelectConfig: SelectConfigItem[] =
      normalizedType === 'Upload'
        ? nextSelectConfigBase
        : [
            ...nextSelectConfigBase,
            {
              key: normalizedKey,
              dialogTitle: normalizedDialogTitle || normalizedName,
              options: cleanedOptions
            }
          ]

    if (normalizedType !== 'Upload') {
      upsertOptionLibrary({
        key: normalizedKey,
        dialogTitle: normalizedDialogTitle || normalizedName,
        options: cleanedOptions
      })
    }

    const nextData: SkillDataPayload = {
      ...currentData,
      components: nextComponents,
      selectConfig: nextSelectConfig
    }

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(nextData, null, 2)
    }))

    setComponentForm(defaultComponentForm)
    setComponentFormError('')
    setEditingComponentIndex(null)
    setIsComponentIconUploading(false)
    setUploadingOptionIndex(null)
    setIsComponentModalOpen(false)
  }

  const handleAddPresetComponent = (preset: PresetSkillComponent) => {
    const currentData = parseSkillData(skillInfo.data)
    const currentComponents = Array.isArray(currentData.components) ? currentData.components : []
    const currentSelectConfig = Array.isArray(currentData.selectConfig) ? currentData.selectConfig : []

    if (currentComponents.some((item) => item.key === preset.component.key)) {
      window.alert(`组件 key "${preset.component.key}" 已存在`)
      return
    }

    const nextComponents = [...currentComponents, preset.component]
    const nextSelectConfig =
      preset.component.type !== 'Upload' && preset.selectConfig
        ? [
            ...currentSelectConfig.filter((item) => item.key !== preset.selectConfig?.key),
            {
              ...preset.selectConfig,
              options: cloneOptions(preset.selectConfig.options)
            }
          ]
        : currentSelectConfig

    const nextData: SkillDataPayload = {
      ...currentData,
      components: nextComponents,
      selectConfig: nextSelectConfig
    }

    if (preset.component.type !== 'Upload' && preset.selectConfig) {
      upsertOptionLibrary({
        ...preset.selectConfig,
        options: cloneOptions(preset.selectConfig.options)
      })
    }

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(nextData, null, 2)
    }))

    setComponentValues((prev) => ({
      ...prev,
      [preset.component.key]: prev[preset.component.key] ?? preset.component.id
    }))
  }

  const resetTemplateForm = () => {
    setTemplateForm(createDefaultTemplateForm())
    setTemplateFormError('')
    setEditingTemplateIndex(null)
    setIsTemplateCoverUploading(false)
    setIsTemplateNodeEditorOpen(false)
    setEditingTemplateNodeUid(null)
    setTemplateNodeEditor(createDefaultTemplateNodeEditor('Input'))
    setTemplateNodeEditorError('')
    setUploadingTemplateNodeOptionIndex(null)
  }

  const handleOpenTemplateForm = () => {
    resetTemplateForm()
    setIsTemplateFormOpen(true)
  }

  const handleOpenTemplateEditor = (index: number) => {
    const targetTemplate = skillTemplates[index]
    if (!targetTemplate) return

    setTemplateForm({
      title: targetTemplate.title,
      id: targetTemplate.id,
      cover: targetTemplate.cover,
      desc: targetTemplate.desc,
      nodes: templatePayloadToDraftNodes(targetTemplate.template, selectConfigMap)
    })
    setTemplateFormError('')
    setEditingTemplateIndex(index)
    setIsTemplateCoverUploading(false)
    setIsTemplateNodeEditorOpen(false)
    setEditingTemplateNodeUid(null)
    setTemplateNodeEditor(createDefaultTemplateNodeEditor('Input'))
    setTemplateNodeEditorError('')
    setUploadingTemplateNodeOptionIndex(null)
    setIsTemplateFormOpen(true)
  }

  const handleApplyTemplateToSender = (template: SkillTemplateItem) => {
    setMainSenderChatNode(templatePayloadToChatNodes(template.template))
    setActiveTemplateId(template.id)
    setIsTemplatePanelOpen(false)
  }

  const handleDeleteTemplate = (index: number) => {
    const targetTemplate = skillTemplates[index]
    if (!targetTemplate) return
    if (!window.confirm(`确认删除模板「${targetTemplate.title || targetTemplate.id || '未命名模板'}」吗？`)) return

    const currentData = parseSkillData(skillInfo.data)
    const currentTemplates = Array.isArray(currentData.templates) ? currentData.templates : []
    const nextTemplates = currentTemplates.filter((_, i) => i !== index)

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(
        {
          ...currentData,
          templates: nextTemplates
        },
        null,
        2
      )
    }))

    if (activeTemplateId === targetTemplate.id) {
      setActiveTemplateId('')
    }
  }

  const handleReorderTemplates = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return
    const currentData = parseSkillData(skillInfo.data)
    const currentTemplates = Array.isArray(currentData.templates) ? [...currentData.templates] : []
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= currentTemplates.length || toIndex >= currentTemplates.length) return

    const [moved] = currentTemplates.splice(fromIndex, 1)
    currentTemplates.splice(toIndex, 0, moved)

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(
        {
          ...currentData,
          templates: currentTemplates
        },
        null,
        2
      )
    }))
  }

  const handleTemplateCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsTemplateCoverUploading(true)
    try {
      const url = await ossClient.upload(file, 'skill/template-cover')
      setTemplateForm((prev) => ({
        ...prev,
        cover: url
      }))
    } catch (error) {
      console.error('Template cover upload failed:', error)
    } finally {
      setIsTemplateCoverUploading(false)
      e.target.value = ''
    }
  }

  const handleOpenTemplateNodeEditor = (type: 'Input' | 'Select', nodeUid?: string) => {
    if (!nodeUid) {
      setTemplateNodeEditor(createDefaultTemplateNodeEditor(type))
      setEditingTemplateNodeUid(null)
      setTemplateNodeEditorError('')
      setUploadingTemplateNodeOptionIndex(null)
      setIsTemplateNodeEditorOpen(true)
      return
    }

    const latestNodes = getLatestTemplateNodes()
    const targetNode = latestNodes.find((node) => node.uid === nodeUid)
    if (!targetNode || targetNode.type === 'Write') return

    if (targetNode.type === 'Input') {
      setTemplateNodeEditor({
        type: 'Input',
        key: targetNode.key,
        placeholder: targetNode.placeholder,
        text: targetNode.text,
        id: '',
        name: '',
        dialogTitle: '',
        options: [createEmptyOption()]
      })
    } else {
      setTemplateNodeEditor({
        type: 'Select',
        key: targetNode.key,
        placeholder: '',
        text: '',
        id: targetNode.id,
        name: targetNode.name,
        dialogTitle: targetNode.dialogTitle,
        options: targetNode.options.length
          ? targetNode.options.map((item) => ({
              id: item.id,
              name: item.name,
              icon: item.icon,
              desc: item.desc
            }))
          : [createEmptyOption()]
      })
    }

    setEditingTemplateNodeUid(nodeUid)
    setTemplateNodeEditorError('')
    setUploadingTemplateNodeOptionIndex(null)
    setIsTemplateNodeEditorOpen(true)
  }

  const handleTemplateNodeOptionChange = (index: number, field: keyof SelectOption, value: string) => {
    setTemplateNodeEditor((prev) => ({
      ...prev,
      options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
    }))
  }

  const handleAddTemplateNodeOption = () => {
    setTemplateNodeEditor((prev) => ({
      ...prev,
      options: [...prev.options, createEmptyOption()]
    }))
  }

  const handleRemoveTemplateNodeOption = (index: number) => {
    setTemplateNodeEditor((prev) => ({
      ...prev,
      options: prev.options.length === 1 ? prev.options : prev.options.filter((_, itemIndex) => itemIndex !== index)
    }))
  }

  const handleTemplateNodeOptionIconUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingTemplateNodeOptionIndex(index)
    try {
      const url = await ossClient.upload(file, 'skill/options')
      setTemplateNodeEditor((prev) => ({
        ...prev,
        options: prev.options.map((item, itemIndex) => (itemIndex === index ? { ...item, icon: url } : item))
      }))
    } catch (error) {
      console.error('Template node option icon upload failed:', error)
    } finally {
      setUploadingTemplateNodeOptionIndex(null)
      e.target.value = ''
    }
  }

  const getLatestTemplateNodes = (): TemplateNodeDraft[] => {
    const sender = templateSenderRef.current
    if (!sender) return templateFormRef.current.nodes
    return chatNodesToTemplateNodes(sender.getModel(), templateFormRef.current.nodes)
  }

  const handleSaveTemplateNode = () => {
    const normalizedKey = templateNodeEditor.key.trim()
    if (!normalizedKey) {
      setTemplateNodeEditorError('组件 key 必填')
      return
    }

    const latestNodes = getLatestTemplateNodes()

    if (templateNodeEditor.type === 'Input') {
      const nextNode: TemplateInputNodeDraft = {
        uid: editingTemplateNodeUid ?? createUid(),
        type: 'Input',
        key: normalizedKey,
        placeholder: templateNodeEditor.placeholder.trim(),
        text: templateNodeEditor.text
      }

      const hasDuplicateKey = latestNodes.some((node) => node.type !== 'Write' && node.key === normalizedKey && node.uid !== nextNode.uid)
      if (hasDuplicateKey) {
        setTemplateNodeEditorError(`模板节点 key "${normalizedKey}" 已存在`)
        return
      }
      const nextNodes = editingTemplateNodeUid ? latestNodes.map((node) => (node.uid === editingTemplateNodeUid ? nextNode : node)) : [...latestNodes, nextNode]
      setTemplateForm((prev) => ({
        ...prev,
        nodes: nextNodes
      }))

      setIsTemplateNodeEditorOpen(false)
      setEditingTemplateNodeUid(null)
      setTemplateNodeEditor(createDefaultTemplateNodeEditor('Input'))
      setTemplateNodeEditorError('')
      setUploadingTemplateNodeOptionIndex(null)
      return
    }

    const normalizedId = templateNodeEditor.id.trim()
    const normalizedName = templateNodeEditor.name.trim()
    if (!normalizedId || !normalizedName) {
      setTemplateNodeEditorError('下拉框组件的 id 和 name 必填')
      return
    }

    const cleanedOptions = templateNodeEditor.options
      .map((item) => ({
        id: item.id.trim(),
        name: item.name.trim(),
        icon: item.icon.trim(),
        desc: item.desc.trim()
      }))
      .filter((item) => item.id || item.name || item.icon || item.desc)

    if (cleanedOptions.length === 0) {
      setTemplateNodeEditorError('下拉框组件至少需要一个可选项')
      return
    }

    if (cleanedOptions.some((item) => !item.id || !item.name)) {
      setTemplateNodeEditorError('可选项的 id 和 name 为必填')
      return
    }

    const nextNode: TemplateSelectNodeDraft = {
      uid: editingTemplateNodeUid ?? createUid(),
      type: 'Select',
      key: normalizedKey,
      id: normalizedId,
      name: normalizedName,
      dialogTitle: templateNodeEditor.dialogTitle.trim() || normalizedName,
      options: cleanedOptions
    }

    upsertOptionLibrary({
      key: normalizedKey,
      dialogTitle: templateNodeEditor.dialogTitle.trim() || normalizedName,
      options: cleanedOptions
    })

    const hasDuplicateKey = latestNodes.some((node) => node.type !== 'Write' && node.key === normalizedKey && node.uid !== nextNode.uid)
    if (hasDuplicateKey) {
      setTemplateNodeEditorError(`模板节点 key "${normalizedKey}" 已存在`)
      return
    }
    const nextNodes = editingTemplateNodeUid ? latestNodes.map((node) => (node.uid === editingTemplateNodeUid ? nextNode : node)) : [...latestNodes, nextNode]
    setTemplateForm((prev) => ({
      ...prev,
      nodes: nextNodes
    }))

    setIsTemplateNodeEditorOpen(false)
    setEditingTemplateNodeUid(null)
    setTemplateNodeEditor(createDefaultTemplateNodeEditor('Input'))
    setTemplateNodeEditorError('')
    setUploadingTemplateNodeOptionIndex(null)
  }

  const handleSubmitTemplate = () => {
    const normalizedTitle = templateForm.title.trim()
    const normalizedId = templateForm.id.trim()
    if (!normalizedTitle || !normalizedId) {
      setTemplateFormError('标题和 ID 为必填')
      return
    }

    const senderModel = templateSenderRef.current?.getModel()
    const nodesForSubmit = senderModel ? chatNodesToTemplateNodes(senderModel, templateForm.nodes) : templateForm.nodes

    const serializedTemplateNodes: Array<Record<string, unknown>> = []
    const templateSelectConfigs: SelectConfigItem[] = []

    for (const node of nodesForSubmit) {
      if (node.type === 'Write') {
        if (node.text.trim()) {
          serializedTemplateNodes.push({
            type: 'Write',
            text: node.text
          })
        }
        continue
      }

      if (node.type === 'Input') {
        const key = node.key.trim()
        if (!key) {
          setTemplateFormError('模板中输入框组件的 key 不能为空')
          return
        }
        serializedTemplateNodes.push({
          type: 'Input',
          key,
          placeholder: node.placeholder.trim(),
          text: node.text
        })
        continue
      }

      const key = node.key.trim()
      const id = node.id.trim()
      const name = node.name.trim()
      if (!key || !id || !name) {
        setTemplateFormError('模板中下拉框组件的 key、id、name 不能为空')
        return
      }

      const cleanedOptions = node.options
        .map((item) => ({
          id: item.id.trim(),
          name: item.name.trim(),
          icon: item.icon.trim(),
          desc: item.desc.trim()
        }))
        .filter((item) => item.id || item.name || item.icon || item.desc)

      if (cleanedOptions.length === 0 || cleanedOptions.some((item) => !item.id || !item.name)) {
        setTemplateFormError(`模板下拉框 "${name}" 的 options 不完整`)
        return
      }

      serializedTemplateNodes.push({
        type: 'Select',
        key,
        id,
        name
      })
      templateSelectConfigs.push({
        key,
        dialogTitle: node.dialogTitle.trim() || name,
        options: cleanedOptions
      })
    }

    if (serializedTemplateNodes.length === 0) {
      if (nodesForSubmit.length === 0) {
        setTemplateFormError('请至少编辑一段模板内容')
        return
      }
      serializedTemplateNodes.push({ type: 'Write', text: '' })
    }

    const currentData = parseSkillData(skillInfo.data)
    const currentTemplates = Array.isArray(currentData.templates) ? currentData.templates : []
    const currentSelectConfig = Array.isArray(currentData.selectConfig) ? currentData.selectConfig : []

    const hasDuplicateTemplateId = currentTemplates.some((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return false
      if (editingTemplateIndex !== null && index === editingTemplateIndex) return false
      return String((item as Record<string, unknown>).id ?? '') === normalizedId
    })
    if (hasDuplicateTemplateId) {
      setTemplateFormError(`模板 ID "${normalizedId}" 已存在`)
      return
    }

    const templateSelectKeys = new Set(templateSelectConfigs.map((item) => item.key))
    const nextSelectConfig: SelectConfigItem[] = [...currentSelectConfig.filter((item) => !templateSelectKeys.has(item.key)), ...templateSelectConfigs]

    const nextTemplateItem = {
      title: normalizedTitle,
      id: normalizedId,
      cover: templateForm.cover.trim(),
      desc: templateForm.desc.trim(),
      template: serializedTemplateNodes
    }
    const nextTemplates = editingTemplateIndex === null ? [...currentTemplates, nextTemplateItem] : currentTemplates.map((item, index) => (index === editingTemplateIndex ? nextTemplateItem : item))

    const nextData: SkillDataPayload = {
      ...currentData,
      templates: nextTemplates,
      selectConfig: nextSelectConfig
    }

    setSkillInfo((prev) => ({
      ...prev,
      data: JSON.stringify(nextData, null, 2)
    }))
    setMainSenderChatNode(templatePayloadToChatNodes(serializedTemplateNodes))
    setActiveTemplateId(normalizedId)

    setTemplateFormError('')
    setIsTemplateFormOpen(false)
    setIsTemplatePanelOpen(true)
    resetTemplateForm()
  }

  const handleComponentValueChange = (componentKey: string, value: string) => {
    setComponentValues((prev) => ({
      ...prev,
      [componentKey]: value
    }))
  }

  const handleComponentUploadChange = (componentKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const previewUrl = URL.createObjectURL(file)
    setComponentUploadPreview((prev) => {
      const previousPreview = prev[componentKey]
      if (previousPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(previousPreview)
      }
      return {
        ...prev,
        [componentKey]: previewUrl
      }
    })
    e.target.value = ''
  }

  const renderSkillComponent = (component: SkillComponent) => {
    const config = selectConfigMap[component.key]
    const options = config?.options?.length ? config.options : [{ id: component.id, name: component.name, icon: '', desc: '' }]
    const selectedValue = componentValues[component.key] ?? component.id ?? ''

    if (component.type === 'Radio') {
      return (
        <div className="inline-flex items-center rounded-lg bg-gray-50 p-1 ring-1 ring-gray-200">
          {options.map((option) => {
            const isActive = selectedValue === option.id
            return (
              <button
                key={`${component.key}-${option.id}`}
                type="button"
                onClick={() => handleComponentValueChange(component.key, option.id)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-white'}`}
              >
                {option.name || option.id}
              </button>
            )
          })}
        </div>
      )
    }

    if (component.type === 'Select') {
      const selectedOption = options.find((option) => option.id === selectedValue) ?? options[0]
      return (
        <div className="inline-flex items-center rounded-lg bg-gray-50 px-2 py-1.5 ring-1 ring-gray-200">
          {isImageUrl(component.icon) ? <img src={component.icon} alt={component.name || '组件图标'} className="h-5 w-5 rounded object-cover" /> : null}
          <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
              <button type="button" className="inline-flex h-7 min-w-[160px] items-center justify-between gap-2 rounded-md px-2 text-sm text-gray-700 transition-colors hover:bg-white">
                <span className="truncate">{component.name}</span>
                <FiChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="start" sideOffset={4} className="z-[90] min-w-[220px] overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-md">
                {options.map((option) => (
                  <DropdownMenu.Item
                    key={`${component.key}-${option.id}`}
                    onSelect={() => handleComponentValueChange(component.key, option.id)}
                    className="cursor-pointer rounded-md px-2.5 py-2 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                  >
                    <div className="flex items-start gap-2">
                      {isImageUrl(option.icon) ? <img src={option.icon} alt={option.name || '选项图标'} className="mt-0.5 h-4 w-4 rounded object-cover" /> : null}
                      <span className="min-w-0">
                        <span className="block truncate text-xs text-gray-700">{option.name || option.id}</span>
                        {option.desc ? <span className="mt-0.5 block truncate text-[11px] text-gray-400">{option.desc}</span> : null}
                      </span>
                    </div>
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      )
    }

    return (
      <div className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-gray-50 px-2 py-1.5 ring-1 ring-gray-200">
        {isImageUrl(component.icon) ? <img src={component.icon} alt={component.name || '组件图标'} className="h-5 w-5 rounded object-cover" /> : null}
        {/* <input
          ref={(el) => {
            componentUploadInputRefs.current[component.key] = el
          }}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleComponentUploadChange(component.key, e)}
        /> */}
        <div
        // onClick={() => componentUploadInputRefs.current[component.key]?.click()}
        >
          {component.name}
        </div>
        {/* {componentUploadPreview[component.key] && <img src={componentUploadPreview[component.key]} alt="上传预览" className="h-7 w-7 rounded object-cover ring-1 ring-gray-200" />} */}
      </div>
    )
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-[#f6f8fc]">
      <div className="mx-auto flex h-full w-full justify-end max-w-6xl flex-1 flex-col px-4 py-6 md:px-8">
        <div className="mb-4 flex-1 rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">技能列表</h2>
            {isSkillConfigsLoading ? <span className="text-xs text-gray-400">加载中...</span> : null}
          </div>

          {skillConfigsError ? <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-500">{skillConfigsError}</div> : null}

          {!isSkillConfigsLoading && !skillConfigsError && skillConfigs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-400">暂无技能配置</div>
          ) : null}

          {!skillConfigsError && skillConfigs.length > 0 ? (
            <div className="overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                {skillConfigs.map((skill) => {
                  const isSelected = selectedSkillConfigId === skill.id
                  return (
                    <div
                      key={skill.id}
                      onClick={() => handleSelectSkillFromList(skill)}
                      className={`group flex cursor-pointer items-start gap-4 rounded-2xl border p-2 transition-all ${
                        isSelected ? 'border-blue-200 bg-blue-50/40 shadow-sm' : 'border-gray-100 hover:border-blue-100 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100 text-lg font-medium text-gray-600">
                        {isImageUrl(skill.avatar) ? <img src={skill.avatar} alt={skill.name} className="h-full w-full object-cover" /> : <span>{skill.name.slice(0, 1)}</span>}
                      </div>
                      <div className="flex flex-1 flex-col justify-center overflow-hidden">
                        <div className="mb-1 truncate text-[14px] font-medium text-gray-900">{skill.name}</div>
                        <p className="line-clamp-1 text-[12px] text-gray-500">技能ID: {skill.id}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
        </div>

        {selectedSkillConfigId !== null ? (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <button type="button" onClick={handleOpenSkillEditor} className="inline-flex items-center gap-2 rounded-lg bg-[#eaf3ff] px-3 py-2.5 text-[#2b6cb0] transition-colors hover:bg-[#deecff]">
                {isImageUrl(skillInfo.avatar) && !isSkillAvatarLoadFailed ? (
                  <img src={skillInfo.avatar} alt={skillInfo.name || '技能头像'} className="h-4 w-4 rounded object-cover" onError={() => setIsSkillAvatarLoadFailed(true)} />
                ) : (
                  <BsBorderOuter className="h-4 w-4 opacity-10" />
                )}
                {hasSkillData ? skillInfo.name || skillInfo.skillName || '未命名技能' : '添加技能'}
                <FiEdit2 className="h-3.5 w-3.5" />
              </button>
              {skillComponents.map((component, index) => (
                <div key={`${component.key}-${index}`} className="inline-flex items-center gap-1.5">
                  {renderSkillComponent(component)}
                  <DropdownMenu.Root
                    modal={false}
                    open={hoveredComponentMenuIndex === index}
                    onOpenChange={(open) => {
                      if (open) {
                        openComponentMenu(index)
                        return
                      }
                      scheduleCloseComponentMenu(index)
                    }}
                  >
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        ref={(el) => {
                          componentMenuTriggerRefs.current[index] = el
                        }}
                        onPointerEnter={() => openComponentMenu(index)}
                        onPointerLeave={(e) => {
                          if (isPointerInComponentMenu(index, e.relatedTarget)) return
                          scheduleCloseComponentMenu(index)
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-50"
                      >
                        <AiTwotoneSetting className="h-5 w-5" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        ref={(el) => {
                          componentMenuContentRefs.current[index] = el
                        }}
                        align="end"
                        sideOffset={0}
                        onPointerEnter={() => openComponentMenu(index)}
                        onPointerLeave={(e) => {
                          if (isPointerInComponentMenu(index, e.relatedTarget)) return
                          scheduleCloseComponentMenu(index)
                        }}
                        className="z-[80] flex min-w-[108px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-md"
                      >
                        <DropdownMenu.Item
                          onSelect={() => handleOpenComponentEditor(index)}
                          className="cursor-pointer inline-flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-gray-700 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                        >
                          <FiEdit2 className="h-3.5 w-3.5" />
                          修改
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          onSelect={() => handleDeleteComponent(index)}
                          className="cursor-pointer inline-flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-red-500 outline-none transition-colors hover:bg-red-50 focus:bg-red-50"
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                          删除
                        </DropdownMenu.Item>
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>
              ))}
              <button
                type="button"
                onClick={handleOpenComponentModal}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-gray-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
              >
                <FiPlus className="h-6 w-4" />
                添加组件
              </button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-gray-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-50">
                    <FiPlus className="h-6 w-4" />
                    预设组件
                    <FiChevronDown className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="start" sideOffset={8} className="z-[80] min-w-[300px] overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-lg">
                    {presetSkillComponents.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">暂无可用预设组件</div>
                    ) : (
                      presetSkillComponents.map((preset) => {
                        const alreadyAdded = selectedSkillComponentKeys.has(preset.component.key)
                        return (
                          <DropdownMenu.Item
                            key={preset.dedupeKey}
                            onSelect={(event) => {
                              if (alreadyAdded) {
                                event.preventDefault()
                                return
                              }
                              handleAddPresetComponent(preset)
                            }}
                            className="cursor-pointer rounded-lg px-2.5 py-2 outline-none transition-colors hover:bg-gray-50 focus:bg-gray-50"
                          >
                            <div className="flex w-full items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-2">
                                {isImageUrl(preset.component.icon) ? <img src={preset.component.icon} alt={preset.component.name || '组件图标'} className="h-4 w-4 rounded object-cover" /> : null}
                                <div className="min-w-0">
                                  <div className="truncate text-xs font-medium text-gray-700">{preset.component.name || preset.component.key}</div>
                                  <div className="truncate text-[11px] text-gray-400">
                                    {preset.component.type} / {preset.component.key}
                                    {preset.sourceSkillName ? ` · ${preset.sourceSkillName}` : ''}
                                  </div>
                                </div>
                              </div>
                              <span className={`shrink-0 text-[11px] ${alreadyAdded ? 'text-gray-400' : 'text-blue-500'}`}>{alreadyAdded ? '已添加' : '添加'}</span>
                            </div>
                          </DropdownMenu.Item>
                        )
                      })
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
              <button
                type="button"
                onClick={() => setIsTemplatePanelOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-gray-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
              >
                <TbVersionsFilled className="h-6 w-4" />
                模板
              </button>
            </div>
            <div className="flex max-h-[300px] flex-1 flex-col rounded-2xl border border-[#e5eaf1] bg-white shadow-sm">
              <div className="min-h-0 flex-1 p-5 md:p-6">
                <div className="h-full w-full rounded-xl border border-gray-100 bg-white p-3">
                  <div ref={mainSenderElRef} className="h-full min-h-[120px]" />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#edf1f7] px-4 py-3">
                <div className="flex items-center gap-2">
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100">
                    <FiSliders className="h-4 w-4" />
                    思考
                  </button>
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100">
                    <FiSearch className="h-4 w-4" />
                    智能搜索
                  </button>
                </div>

                <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white transition-colors hover:bg-blue-700">
                  <FiArrowUp className="h-4.5 w-4.5" />
                </button>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {isTemplatePanelOpen && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm">
          <div className="w-full max-w-[1400px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">模板列表</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleOpenTemplateForm()
                  }}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <FiPlus className="h-4 w-4" />
                  添加新模板
                </button>
                <button type="button" onClick={() => setIsTemplatePanelOpen(false)} className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                  <FiX className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              {skillTemplates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-400">暂无模板，点击右上角“添加新模板”创建</div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
                  {skillTemplates.map((template, index) => {
                    const templateKey = template.id || `template-${index}`
                    const isActive = activeTemplateId === template.id && !!template.id
                    return (
                      <div
                        key={templateKey}
                        draggable
                        onDragStart={(event) => {
                          setDraggingTemplateIndex(index)
                          event.dataTransfer.effectAllowed = 'move'
                        }}
                        onDragOver={(event) => {
                          event.preventDefault()
                          event.dataTransfer.dropEffect = 'move'
                        }}
                        onDrop={(event) => {
                          event.preventDefault()
                          if (draggingTemplateIndex === null) return
                          handleReorderTemplates(draggingTemplateIndex, index)
                          setDraggingTemplateIndex(null)
                        }}
                        onDragEnd={() => setDraggingTemplateIndex(null)}
                        className={`relative overflow-hidden rounded-2xl shadow-sm transition-all hover:-translate-y-0.5 hover:shadow ${isActive ? 'ring-2 ring-blue-500' : 'ring-1 ring-gray-200'} ${draggingTemplateIndex === index ? 'opacity-60' : ''}`}
                      >
                        <div className="absolute right-2 top-2 z-20 flex items-center gap-1.5">
                          <button type="button" onClick={() => handleOpenTemplateEditor(index)} className="rounded-md bg-white/90 px-2 py-1 text-xs text-gray-700 transition-colors hover:bg-white">
                            编辑
                          </button>
                          <button type="button" onClick={() => handleDeleteTemplate(index)} className="rounded-md bg-white/90 px-2 py-1 text-xs text-red-500 transition-colors hover:bg-white">
                            删除
                          </button>
                        </div>
                        <button type="button" onClick={() => handleApplyTemplateToSender(template)} className="block w-full text-left">
                          <div
                            className={`relative h-[190px] p-4 ${isImageUrl(template.cover) ? '' : 'bg-gradient-to-br from-gray-100 via-gray-50 to-white'}`}
                            style={isImageUrl(template.cover) ? { backgroundImage: `url(${template.cover})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                          >
                            {isImageUrl(template.cover) && <div className="absolute inset-0 bg-black/35" />}
                            <div className="relative z-10 mt-16 rounded-lg bg-black/45 p-3 text-white backdrop-blur-[1px]">
                              <div className="truncate text-sm font-semibold">{template.title || '未命名模板'}</div>
                              <div className="mt-1 line-clamp-2 text-xs leading-5 text-white/90">{template.desc || '暂无描述'}</div>
                            </div>
                          </div>
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isSkillModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">编辑技能信息</h2>
              <button type="button" onClick={() => setIsSkillModalOpen(false)} className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能标题（name）
                  <input
                    value={draftSkillInfo.name}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, name: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入技能标题"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能ID（id）
                  <input
                    value={draftSkillInfo.skillId}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, skillId: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：music"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能名称（skillName）
                  <input
                    value={draftSkillInfo.skillName}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, skillName: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入技能名称"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  技能描述（description）
                  <textarea
                    value={draftSkillInfo.description}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, description: e.target.value }))}
                    className="min-h-[88px] rounded-lg border border-gray-200 px-3 py-2 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入技能描述"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  技能头像（avatar）
                  <div className="flex gap-2">
                    <input
                      value={draftSkillInfo.avatar}
                      onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, avatar: e.target.value }))}
                      className="h-10 flex-1 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="请输入头像 URL 或上传图片"
                    />
                    <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isAvatarUploading}
                      className="h-10 shrink-0 rounded-lg border flex items-center gap-1 border-gray-200 px-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FiUpload className="h-4 w-4" />
                      {isAvatarUploading ? '上传中...' : '上传头像'}
                    </button>
                  </div>
                  {draftSkillInfo.avatar && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={draftSkillInfo.avatar} alt="技能头像预览" className="h-10 w-10 rounded-md object-cover ring-1 ring-gray-200" />
                      <span className="max-w-[420px] truncate text-xs text-gray-500">{draftSkillInfo.avatar}</span>
                    </div>
                  )}
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能分类ID（categoryId）
                  <select
                    value={String(draftSkillInfo.categoryId)}
                    onChange={(e) => handleCategoryChange(Number(e.target.value))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                  >
                    <option value="0">{isCategoryLoading ? '分类加载中...' : '请选择技能分类'}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}（ID: {category.id}）
                      </option>
                    ))}
                  </select>
                  {categoryError && <span className="text-xs text-red-500">{categoryError}</span>}
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能分类名称（categoryName）
                  <input
                    value={draftSkillInfo.categoryName ?? ''}
                    readOnly
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="将随分类自动填写"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  技能排序（sort）
                  <input
                    type="number"
                    value={draftSkillInfo.sort}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, sort: Number(e.target.value) }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入排序"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  默认模板ID（defaultTemplateId）
                  <input
                    value={draftSkillInfo.defaultTemplateId}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, defaultTemplateId: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：music_template_1"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  输入框占位（placeholder）
                  <input
                    value={draftSkillInfo.placeholder}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, placeholder: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：描述你想要的歌曲"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  顶部快捷区展示（topShow）
                  <select
                    value={String(draftSkillInfo.topShow)}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, topShow: e.target.value === 'true' }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </label>

                <div className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  <span>可用模型（data.models，多选）</span>
                  {agentModelListLoading ? (
                    <span className="text-xs text-gray-500">加载模型列表…</span>
                  ) : agentModelList.length === 0 ? (
                    <span className="text-xs text-gray-500">暂无模型，请确认已登录且接口可用</span>
                  ) : (
                    <div className="max-h-44 overflow-y-auto rounded-lg border border-gray-200 p-2">
                      {agentModelList.map((m) => (
                        <label key={m.modelId} className="flex cursor-pointer items-center gap-2 rounded-md py-1 pr-2 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={draftDataModels.includes(m.modelId)}
                            onChange={() => toggleDraftDataModel(m.modelId)}
                          />
                          {isImageUrl(m.modelIcon) ? (
                            <img src={m.modelIcon} alt="" className="h-4 w-4 shrink-0 rounded-md object-cover ring-1 ring-gray-200" />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                              {(m.name || m.modelId).charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="min-w-0 text-sm text-gray-800">{m.name}</span>
                          <span className="shrink-0 text-xs text-gray-500">({m.modelId})</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  技能数据（data）
                  <textarea
                    value={draftSkillInfo.data}
                    onChange={(e) => setDraftSkillInfo((prev) => ({ ...prev, data: e.target.value }))}
                    className="min-h-[88px] rounded-lg border border-gray-200 px-3 py-2 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入技能数据"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  技能结构（导出/粘贴应用）
                  <textarea
                    value={structureEditorValue}
                    onChange={(e) => setStructureEditorValue(e.target.value)}
                    className="min-h-[200px] rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder='粘贴技能结构对象，例如 { "id": 16, "name": "视频生成", "skillName": "seedance", "data": "" }'
                  />
                </label>
                <div className="flex items-center gap-2 md:col-span-2">
                  <button type="button" onClick={handleFillStructureFromCurrent} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50">
                    填充当前结构
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyPastedStructure}
                    className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    应用粘贴结构
                  </button>
                  {structureEditorError && <span className="text-xs text-red-500">{structureEditorError}</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetDefaultSkillData}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 transition-colors hover:bg-amber-100"
                >
                  加载默认数据
                </button>
                <button type="button" onClick={handleClearSkillData} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50">
                  清空数据
                </button>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setIsSkillModalOpen(false)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50">
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveSkillInfo()}
                  disabled={isSavingSkillData}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingSkillData ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isComponentModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">{editingComponentIndex === null ? '添加组件' : '修改组件'}</h2>
              <button
                type="button"
                onClick={() => {
                  setIsComponentModalOpen(false)
                  setEditingComponentIndex(null)
                  setIsComponentIconUploading(false)
                  setUploadingOptionIndex(null)
                }}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  执行类型（actionType）
                  <select
                    value={componentForm.actionType}
                    onChange={(e) =>
                      setComponentForm((prev) => ({
                        ...prev,
                        actionType: e.target.value as ActionType
                      }))
                    }
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                  >
                    <option value="">请选择执行类型</option>
                    {actionTypeOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  组件类型（type）
                  <select
                    value={componentForm.type}
                    disabled={!componentForm.actionType}
                    onChange={(e) =>
                      setComponentForm((prev) => ({
                        ...prev,
                        type: e.target.value as ComponentType
                      }))
                    }
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">请选择组件类型</option>
                    {componentTypeOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  key
                  <input
                    value={componentForm.key}
                    onChange={(e) => setComponentForm((prev) => ({ ...prev, key: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：music_style"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  id
                  <input
                    value={componentForm.id}
                    onChange={(e) => setComponentForm((prev) => ({ ...prev, id: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：pop"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  name
                  <input
                    value={componentForm.name}
                    onChange={(e) => setComponentForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：流行"
                  />
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  icon
                  <div className="flex gap-2">
                    <input
                      value={componentForm.icon}
                      onChange={(e) => setComponentForm((prev) => ({ ...prev, icon: e.target.value }))}
                      className="h-10 flex-1 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="组件图标 URL（可选）"
                    />
                    <input ref={componentIconInputRef} type="file" accept="image/*" className="hidden" onChange={handleComponentIconUpload} />
                    <button
                      type="button"
                      onClick={() => componentIconInputRef.current?.click()}
                      disabled={isComponentIconUploading}
                      className="h-10 shrink-0 inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <FiUpload className="h-4 w-4" />
                      {isComponentIconUploading ? '上传中...' : '上传图标'}
                    </button>
                  </div>
                  {componentForm.icon && (
                    <div className="mt-1 flex items-center gap-2">
                      {isImageUrl(componentForm.icon) && <img src={componentForm.icon} alt="组件图标预览" className="h-7 w-7 rounded object-cover ring-1 ring-gray-200" />}
                      <span className="truncate text-xs text-gray-500">{componentForm.icon}</span>
                    </div>
                  )}
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  dialogTitle（对应 selectConfig）
                  <input
                    value={componentForm.dialogTitle}
                    onChange={(e) => setComponentForm((prev) => ({ ...prev, dialogTitle: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：音乐风格"
                  />
                </label>
              </div>

              {componentForm.type !== 'Upload' && (
                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const groupKey = e.target.value
                        if (!groupKey) return
                        handleApplyOptionGroupToComponent(groupKey)
                        e.currentTarget.value = ''
                      }}
                      className="h-9 min-w-[220px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition-colors focus:border-blue-500"
                    >
                      <option value="">选择已创建 options 组</option>
                      {optionLibrary.map((item) => (
                        <option key={`component-option-group-${item.key}`} value={item.key}>
                          {item.dialogTitle || item.key}（{item.key}）
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => openOptionGroupModal('component')}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      管理 options 组
                    </button>
                  </div>
                  <OptionsEditor
                    options={componentForm.options}
                    uploadingIndex={uploadingOptionIndex}
                    onAdd={handleAddComponentOption}
                    onRemove={handleRemoveComponentOption}
                    onChange={handleComponentOptionChange}
                    onUpload={handleOptionIconUpload}
                  />
                </div>
              )}

              {componentFormError && <p className="mt-3 text-sm text-red-500">{componentFormError}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setIsComponentModalOpen(false)
                  setEditingComponentIndex(null)
                  setIsComponentIconUploading(false)
                  setUploadingOptionIndex(null)
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                取消
              </button>
              <button type="button" onClick={handleConfirmAddComponent} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700">
                {editingComponentIndex === null ? '确定添加' : '保存修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isTemplateFormOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">{editingTemplateIndex === null ? '添加模板' : '编辑模板'}</h2>
              <button
                type="button"
                onClick={() => {
                  setIsTemplateFormOpen(false)
                  setIsTemplateNodeEditorOpen(false)
                  setEditingTemplateNodeUid(null)
                  setTemplateNodeEditorError('')
                  setUploadingTemplateNodeOptionIndex(null)
                }}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  标题（title）
                  <input
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, title: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入模板标题"
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  ID（id）
                  <div className="flex gap-2">
                    <input
                      value={templateForm.id}
                      onChange={(e) => setTemplateForm((prev) => ({ ...prev, id: e.target.value }))}
                      className="h-10 flex-1 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="请输入模板ID"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const existingIds = new Set(skillTemplates.map((item) => item.id).filter(Boolean))
                        const nextId = createRandomTemplateId(existingIds)
                        setTemplateForm((prev) => ({ ...prev, id: nextId }))
                      }}
                      className="h-10 shrink-0 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      生成ID
                    </button>
                  </div>
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  封面（cover）
                  <div className="flex gap-2">
                    <input
                      value={templateForm.cover}
                      onChange={(e) => setTemplateForm((prev) => ({ ...prev, cover: e.target.value }))}
                      className="h-10 flex-1 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="请输入封面 URL 或上传图片"
                    />
                    <input ref={templateCoverInputRef} type="file" accept="image/*" className="hidden" onChange={handleTemplateCoverUpload} />
                    <button
                      type="button"
                      onClick={() => templateCoverInputRef.current?.click()}
                      disabled={isTemplateCoverUploading}
                      className="h-10 shrink-0 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isTemplateCoverUploading ? '上传中...' : '上传封面'}
                    </button>
                  </div>
                </label>

                <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                  描述（desc）
                  <textarea
                    value={templateForm.desc}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, desc: e.target.value }))}
                    className="min-h-[88px] rounded-lg border border-gray-200 px-3 py-2 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="请输入模板描述"
                  />
                </label>
              </div>

              <div className="mt-5">
                <div className="mb-2 text-sm font-medium text-gray-700">模板编辑器</div>
                <div className="min-h-[260px] rounded-xl border border-gray-200 bg-white p-4">
                  <div ref={templateSenderElRef} className="min-h-[120px]" />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenTemplateNodeEditor('Select')}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    添加下拉框
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenTemplateNodeEditor('Input')}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    添加输入框
                  </button>
                </div>
              </div>

              {templateFormError && <p className="mt-3 text-sm text-red-500">{templateFormError}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setIsTemplateFormOpen(false)
                  setIsTemplateNodeEditorOpen(false)
                  setEditingTemplateNodeUid(null)
                  setTemplateNodeEditorError('')
                  setUploadingTemplateNodeOptionIndex(null)
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                取消
              </button>
              <button type="button" onClick={handleSubmitTemplate} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700">
                {editingTemplateIndex === null ? '确定添加模板' : '保存模板'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isTemplateNodeEditorOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm" onClick={() => setIsTemplateNodeEditorOpen(false)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                {editingTemplateNodeUid ? '编辑' : '添加'}
                {templateNodeEditor.type === 'Select' ? '下拉框组件' : '输入框组件'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsTemplateNodeEditorOpen(false)
                  setEditingTemplateNodeUid(null)
                  setTemplateNodeEditorError('')
                  setUploadingTemplateNodeOptionIndex(null)
                }}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  key
                  <input
                    value={templateNodeEditor.key}
                    onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, key: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：music_style"
                  />
                </label>

                {templateNodeEditor.type === 'Input' ? (
                  <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                    placeholder
                    <input
                      value={templateNodeEditor.placeholder}
                      onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, placeholder: e.target.value }))}
                      className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="例如：描述歌词要表达的主题"
                    />
                  </label>
                ) : (
                  <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                    id
                    <input
                      value={templateNodeEditor.id}
                      onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, id: e.target.value }))}
                      className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="例如：pop"
                    />
                  </label>
                )}

                {templateNodeEditor.type === 'Input' ? (
                  <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                    默认文案（text）
                    <input
                      value={templateNodeEditor.text}
                      onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, text: e.target.value }))}
                      className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                      placeholder="例如：默认值"
                    />
                  </label>
                ) : (
                  <>
                    <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                      name
                      <input
                        value={templateNodeEditor.name}
                        onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, name: e.target.value }))}
                        className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                        placeholder="例如：流行"
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-sm text-gray-600 md:col-span-2">
                      dialogTitle
                      <input
                        value={templateNodeEditor.dialogTitle}
                        onChange={(e) => setTemplateNodeEditor((prev) => ({ ...prev, dialogTitle: e.target.value }))}
                        className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                        placeholder="例如：音乐风格"
                      />
                    </label>
                  </>
                )}
              </div>

              {templateNodeEditor.type === 'Select' && (
                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const groupKey = e.target.value
                        if (!groupKey) return
                        handleApplyOptionGroupToTemplate(groupKey)
                        e.currentTarget.value = ''
                      }}
                      className="h-9 min-w-[220px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition-colors focus:border-blue-500"
                    >
                      <option value="">选择已创建 options 组</option>
                      {optionLibrary.map((item) => (
                        <option key={`template-option-group-${item.key}`} value={item.key}>
                          {item.dialogTitle || item.key}（{item.key}）
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => openOptionGroupModal('template')}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      管理 options 组
                    </button>
                  </div>
                  <OptionsEditor
                    title="下拉框 options"
                    options={templateNodeEditor.options}
                    uploadingIndex={uploadingTemplateNodeOptionIndex}
                    onAdd={handleAddTemplateNodeOption}
                    onRemove={handleRemoveTemplateNodeOption}
                    onChange={handleTemplateNodeOptionChange}
                    onUpload={handleTemplateNodeOptionIconUpload}
                  />
                </div>
              )}

              {templateNodeEditorError && <p className="mt-3 text-sm text-red-500">{templateNodeEditorError}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setIsTemplateNodeEditorOpen(false)
                  setEditingTemplateNodeUid(null)
                  setTemplateNodeEditorError('')
                  setUploadingTemplateNodeOptionIndex(null)
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                取消
              </button>
              <button type="button" onClick={handleSaveTemplateNode} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700">
                保存组件
              </button>
            </div>
          </div>
        </div>
      )}

      {isOptionGroupModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm" onClick={() => setIsOptionGroupModalOpen(false)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">管理 options 组</h2>
              <button
                type="button"
                onClick={() => {
                  setIsOptionGroupModalOpen(false)
                  setOptionGroupFormError('')
                  setEditingOptionGroupOriginalKey(null)
                  setOptionGroupApplyTarget(null)
                  setUploadingOptionGroupIndex(null)
                }}
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  key
                  <input
                    value={optionGroupForm.key}
                    onChange={(e) => setOptionGroupForm((prev) => ({ ...prev, key: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：music_style"
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm text-gray-600">
                  dialogTitle
                  <input
                    value={optionGroupForm.dialogTitle}
                    onChange={(e) => setOptionGroupForm((prev) => ({ ...prev, dialogTitle: e.target.value }))}
                    className="h-10 rounded-lg border border-gray-200 px-3 text-gray-800 outline-none transition-colors focus:border-blue-500"
                    placeholder="例如：音乐风格"
                  />
                </label>
              </div>

              <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                <OptionsEditor
                  title="options 列表"
                  options={optionGroupForm.options}
                  uploadingIndex={uploadingOptionGroupIndex}
                  onAdd={handleAddOptionGroupOption}
                  onRemove={handleRemoveOptionGroupOption}
                  onChange={handleOptionGroupOptionChange}
                  onUpload={handleOptionGroupIconUpload}
                />
              </div>

              {optionGroupFormError && <p className="mt-3 text-sm text-red-500">{optionGroupFormError}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => {
                  setIsOptionGroupModalOpen(false)
                  setOptionGroupFormError('')
                  setEditingOptionGroupOriginalKey(null)
                  setOptionGroupApplyTarget(null)
                  setUploadingOptionGroupIndex(null)
                }}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              >
                取消
              </button>
              <button type="button" onClick={handleSaveOptionGroup} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700">
                保存 options 组
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
